/* assets/carousel-reviews.js */

class CarouselReviews {
  constructor(element) {
    if (!element) return;
    
    this.wrapper = element;
    this.track = element.querySelector ? element.querySelector('.carousel-reviews__track') : null;
    this.slides = element.querySelectorAll ? Array.from(element.querySelectorAll('.carousel-reviews__slide')) : [];
    this.prevBtn = element.querySelector ? element.querySelector('.carousel-reviews__nav--prev') : null;
    this.nextBtn = element.querySelector ? element.querySelector('.carousel-reviews__nav--next') : null;
    this.indicators = element.querySelectorAll ? Array.from(element.querySelectorAll('.carousel-reviews__indicator')) : [];
    
    // Settings from data attributes
    if (element.dataset) {
    this.autoplay = element.dataset.autoplay === 'true';
          this.speed = (parseInt(element.dataset.speed) || 4) * 1000;
    this.pauseOnHover = element.dataset.pauseHover === 'true';
    this.scrollBehavior = element.dataset.scrollBehavior || 'smooth';
    this.transitionDuration = parseInt(element.dataset.transitionDuration) || 600;
    } else {
      this.autoplay = false;
      this.speed = 4000;
      this.pauseOnHover = false;
      this.scrollBehavior = 'smooth';
      this.transitionDuration = 600;
    }
    
    // Responsive settings
    if (element.dataset) {
    this.reviewsMobile = parseInt(element.dataset.reviewsMobile) || 1;
    this.reviewsTablet = parseInt(element.dataset.reviewsTablet) || 2;
    this.reviewsDesktop = parseInt(element.dataset.reviewsDesktop) || 3;
    } else {
      this.reviewsMobile = 1;
      this.reviewsTablet = 2;
      this.reviewsDesktop = 3;
    }
    
    // Initialize slidesPerView
    this.slidesPerView = this.reviewsMobile;
    this.maxIndex = 0;
    
    // Initialize slide dimensions
    this.slideWidth = 300;
    this.slideGap = 20;
    this.slideWidthWithGap = 320;
    
    // State - ISOLATED BY FUNCTIONALITY
    this.currentIndex = 0;
    this.isTransitioning = false;
    this.autoplayTimer = null;
    this.continuousAnimationId = null;
    this.continuousPosition = 0;
    
    // AUTOPLAY STATE (isolated)
    this.autoplayState = {
      isPaused: false,
      pauseReason: null, // 'hover', 'touch', 'manual', 'visibility'
      resumeQueue: []
    };
    
    // TOUCH NAVIGATION STATE (isolated)
    this.touchNavigationState = {
      isDragging: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      initialTranslate: 0,
      hasMoved: false,
      touchStartTime: 0,
      touchTimeout: null
    };
    
    // MOBILE PARTIAL TOUCH STATE (isolated)
    this.mobileTouchState = {
      isTouching: false,
      isPartialTouchHold: false,
      touchStartTime: 0,
      touchHoldTimer: null,
      lastTouchPosition: null,
      touchMoveDistance: 0,
      isHorizontalScroll: false,
      isVerticalScrolling: false,
      userScrollPosition: null,
      shouldResumeFromUserPosition: false,
      scrollPauseTimer: null
    };
    
    // HOVER STATE (isolated)
    this.hoverState = {
      isHovered: false
    };
    
    // MANUAL NAVIGATION STATE - REMOVED CONFLICTING FLAGS
    
    // ADVANCED DEVICE CAPABILITIES DETECTION
    this.deviceCapabilities = this.detectDeviceCapabilities();
    
    // Legacy compatibility
    this.isMobile = this.detectMobile();
    this.isLowPowerMode = this.detectLowPowerMode();
    this.isSlowConnection = this.detectSlowConnection();
    
    // ADVANCED DEVICE-SPECIFIC OPTIMIZATIONS
    this.optimizations = this.calculateOptimizations();
    
    // Initialize battery info
    this.batteryInfo = null;
    
    // Initialize device multipliers
    this.mobileSlideMultiplier = 0.92;
    this.mobileGapMultiplier = 0.85;
    this.tabletSlideMultiplier = 1.02;
    this.tabletGapMultiplier = 1.05;
    this.desktopSlideMultiplier = 1.02;
    this.desktopGapMultiplier = 1.05;
    this.ultraWideMultiplier = 1.15;
    this.ultraWideGapMultiplier = 1.2;
    this.largeScreenMultiplier = 1.08;
    this.largeScreenGapMultiplier = 1.1;
    this.smallMobileMultiplier = 0.9;
    this.smallMobileGapMultiplier = 0.8;
    this.performanceSlideMultiplier = 1.0;
    this.performanceGapMultiplier = 1.0;
    
    // CRITICAL: LOCK CAROUSEL AGAINST SYSTEM BOLD TEXT SETTINGS
    if (this.deviceCapabilities && typeof this.lockCarouselAgainstSystemSettings === 'function') {
      this.lockCarouselAgainstSystemSettings();
    }
    
    // Initialize
    this.init();
    
    // Initialize image protection
    this.initGlobalProtection();
  }
  
  // ADVANCED DEVICE DETECTION & OPTIMIZATION SYSTEM
  detectDeviceCapabilities() {
    try {
      const capabilities = {
        // Device Type Detection
        deviceType: this.getDeviceType(),
        
        // Hardware Capabilities
        hardware: this.detectHardwareCapabilities(),
        
        // Network Performance
        network: this.detectNetworkCapabilities(),
        
        // Browser Performance
        browser: this.detectBrowserCapabilities(),
        
        // Input Methods
        input: this.detectInputCapabilities(),
        
        // Display Properties
        display: this.detectDisplayCapabilities(),
        
        // Performance Tier
        performanceTier: null // Will be calculated
      };
      
      // Calculate overall performance tier
      capabilities.performanceTier = this.calculatePerformanceTier(capabilities);
      
      return capabilities;
    } catch (e) {
      // Error detecting device capabilities - using fallback
      // Return fallback capabilities
      return {
        deviceType: { type: 'unknown', category: 'mobile' },
        hardware: { cpuCores: 2, memory: null, gpu: { tier: 'standard' } },
        network: { tier: 'standard', effectiveType: 'unknown' },
        browser: { requestAnimationFrame: true, touchEvents: true },
        input: { touch: true, mouse: false, keyboard: true },
        display: { qualityTier: 'standard', pixelRatio: 1 },
        performanceTier: 'standard'
      };
    }
  }
  
  getDeviceType() {
    const userAgent = (navigator && navigator.userAgent) || '';
    const screenWidth = (window && window.screen && window.screen.width) || 1024;
    const screenHeight = (window && window.screen && window.screen.height) || 768;
    const pixelRatio = (window && window.devicePixelRatio) || 1;
    
    // Advanced device detection
    if (/iPhone/.test(userAgent)) {
      return this.detectiPhoneModel(userAgent, screenWidth, screenHeight, pixelRatio);
    }
    
    if (/iPad/.test(userAgent)) {
      return this.detectiPadModel(userAgent, screenWidth, screenHeight, pixelRatio);
    }
    
    if (/Android/.test(userAgent)) {
      return this.detectAndroidDevice(userAgent, screenWidth, screenHeight, pixelRatio);
    }
    
    // Desktop/Laptop detection
    if (screenWidth >= 1024 && !('ontouchstart' in window)) {
      return {
        category: 'desktop',
        type: screenWidth >= 1920 ? 'high-end-desktop' : 'standard-desktop',
        capabilities: ['high-performance', 'precision-input', 'large-screen']
      };
    }
    
    // Fallback mobile detection
    return {
      category: 'mobile',
      type: 'unknown-mobile',
      capabilities: ['touch-input', 'small-screen']
    };
  }
  
  detectiPhoneModel(userAgent, width, height, pixelRatio) {
    // iPhone 15 Pro Max, 14 Pro Max, 13 Pro Max, 12 Pro Max
    if ((width === 430 && height === 932) || (width === 428 && height === 926)) {
      return {
        category: 'mobile',
        type: 'iphone-pro-max',
        model: 'iPhone Pro Max',
        capabilities: ['high-performance', 'premium-display', 'advanced-touch', '120hz-display'],
        optimizations: ['high-quality-animations', 'advanced-gestures', 'premium-effects']
      };
    }
    
    // iPhone 15 Pro, 14 Pro, 13 Pro, 12 Pro
    if ((width === 393 && height === 852) || (width === 390 && height === 844)) {
      return {
        category: 'mobile',
        type: 'iphone-pro',
        model: 'iPhone Pro',
        capabilities: ['high-performance', 'premium-display', 'advanced-touch', '120hz-display'],
        optimizations: ['high-quality-animations', 'advanced-gestures', 'premium-effects']
      };
    }
    
    // iPhone 15, 14, 13, 12
    if (width === 390 && height === 844) {
      return {
        category: 'mobile',
        type: 'iphone-standard',
        model: 'iPhone Standard',
        capabilities: ['good-performance', 'quality-display', 'standard-touch'],
        optimizations: ['standard-animations', 'basic-gestures']
      };
    }
    
    // Older iPhones
    return {
      category: 'mobile',
      type: 'iphone-legacy',
      model: 'iPhone Legacy',
      capabilities: ['limited-performance', 'basic-display', 'basic-touch'],
      optimizations: ['reduced-animations', 'basic-effects', 'performance-mode']
    };
  }
  
  detectiPadModel(userAgent, width, height, pixelRatio) {
    // iPad Pro 12.9", iPad Pro 11"
    if ((width >= 1024 && height >= 1366) || (width >= 834 && height >= 1194)) {
      return {
        category: 'tablet',
        type: 'ipad-pro',
        model: 'iPad Pro',
        capabilities: ['very-high-performance', 'premium-display', 'advanced-touch', 'large-screen', '120hz-display'],
        optimizations: ['desktop-like-experience', 'advanced-gestures', 'premium-effects', 'multi-touch']
      };
    }
    
    // iPad Air, iPad
    return {
      category: 'tablet',
      type: 'ipad-standard',
      model: 'iPad',
      capabilities: ['good-performance', 'quality-display', 'standard-touch', 'medium-screen'],
      optimizations: ['tablet-optimized', 'standard-gestures', 'quality-effects']
    };
  }
  
  detectAndroidDevice(userAgent, width, height, pixelRatio) {
    // High-end Android devices
    if (pixelRatio >= 3 && (width >= 400 || height >= 800)) {
      return {
        category: 'mobile',
        type: 'android-premium',
        model: 'Android Premium',
        capabilities: ['high-performance', 'premium-display', 'advanced-touch'],
        optimizations: ['high-quality-animations', 'advanced-gestures', 'premium-effects']
      };
    }
    
    // Mid-range Android devices
    if (pixelRatio >= 2 && (width >= 360 || height >= 640)) {
      return {
        category: 'mobile',
        type: 'android-mid-range',
        model: 'Android Mid-Range',
        capabilities: ['good-performance', 'quality-display', 'standard-touch'],
        optimizations: ['standard-animations', 'basic-gestures']
      };
    }
    
    // Budget Android devices
    return {
      category: 'mobile',
      type: 'android-budget',
      model: 'Android Budget',
      capabilities: ['limited-performance', 'basic-display', 'basic-touch'],
      optimizations: ['reduced-animations', 'basic-effects', 'performance-mode']
    };
  }
  
  detectHardwareCapabilities() {
    return {
      // CPU Cores
      cpuCores: (navigator && navigator.hardwareConcurrency) || 2,
      
      // Memory (if available)
      memory: (navigator && navigator.deviceMemory) || null,
      
      // GPU Detection (basic)
      gpu: this.detectGPUCapabilities(),
      
      // Battery Status
      battery: this.detectBatteryStatus(),
      
      // Storage Performance
      storage: this.detectStorageCapabilities()
    };
  }
  
  detectGPUCapabilities() {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      
      if (!gl) return { tier: 'none', renderer: 'software' };
      
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'unknown';
      
      // Detect high-end GPUs
      if (renderer.includes('Apple') || renderer.includes('A1') || renderer.includes('M1') || renderer.includes('M2')) {
        return { tier: 'premium', renderer, capabilities: ['hardware-acceleration', 'advanced-effects'] };
      }
      
      if (renderer.includes('Adreno 6') || renderer.includes('Mali-G7') || renderer.includes('PowerVR')) {
        return { tier: 'high', renderer, capabilities: ['hardware-acceleration', 'standard-effects'] };
      }
      
      return { tier: 'standard', renderer, capabilities: ['basic-acceleration'] };
    } catch (e) {
      return { tier: 'unknown', renderer: 'unknown' };
    }
  }
  
  detectBatteryStatus() {
    if (navigator && 'getBattery' in navigator) {
      navigator.getBattery().then(battery => {
        this.batteryInfo = {
          level: battery.level,
          charging: battery.charging,
          chargingTime: battery.chargingTime,
          dischargingTime: battery.dischargingTime
        };
      });
    }
    return { available: (navigator && 'getBattery' in navigator) };
  }
  
  detectStorageCapabilities() {
    if (navigator && 'storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(estimate => {
        this.storageInfo = {
          quota: estimate.quota,
          usage: estimate.usage,
          available: estimate.quota - estimate.usage
        };
      });
    }
    return { available: (navigator && 'storage' in navigator) };
  }
  
  detectNetworkCapabilities() {
    const connection = (navigator && navigator.connection) || (navigator && navigator.mozConnection) || (navigator && navigator.webkitConnection);
    
    if (!connection) {
      return {
        available: false,
        tier: 'unknown',
        effectiveType: 'unknown'
      };
    }
    
    const effectiveType = connection.effectiveType;
    const downlink = connection.downlink;
    const rtt = connection.rtt;
    
    let tier = 'standard';
    let optimizations = ['standard-loading'];
    
    if (effectiveType === '4g' && downlink >= 10) {
      tier = 'premium';
      optimizations = ['high-quality-content', 'preloading', 'advanced-features'];
    } else if (effectiveType === '4g' || (effectiveType === '3g' && downlink >= 1.5)) {
      tier = 'good';
      optimizations = ['quality-content', 'basic-preloading'];
    } else if (effectiveType === '3g' || effectiveType === '2g') {
      tier = 'limited';
      optimizations = ['reduced-quality', 'minimal-loading', 'performance-mode'];
    } else if (effectiveType === 'slow-2g') {
      tier = 'very-limited';
      optimizations = ['ultra-performance-mode', 'minimal-content', 'text-only'];
    }
    
    return {
      available: true,
      effectiveType,
      downlink,
      rtt,
      tier,
      optimizations,
      saveData: connection.saveData || false
    };
  }
  
  detectBrowserCapabilities() {
    const features = {
      // Animation Support
      requestAnimationFrame: 'requestAnimationFrame' in window,
      intersectionObserver: 'IntersectionObserver' in window,
      resizeObserver: 'ResizeObserver' in window,
      
      // Touch Support
      touchEvents: 'ontouchstart' in window,
      pointerEvents: 'PointerEvent' in window,
      
      // Modern JS Features
      es6: this.detectES6Support(),
      webWorkers: 'Worker' in window,
      serviceWorkers: (navigator && 'serviceWorker' in navigator),
      
      // CSS Features
      cssGrid: (CSS && CSS.supports && CSS.supports('display', 'grid')) || false,
      cssFlexbox: (CSS && CSS.supports && CSS.supports('display', 'flex')) || false,
      css3DTransforms: (CSS && CSS.supports && CSS.supports('transform', 'translate3d(0,0,0)')) || false,
      cssCustomProperties: (CSS && CSS.supports && CSS.supports('color', 'var(--test)')) || false,
      
      // Performance APIs
      performanceObserver: 'PerformanceObserver' in window,
      performanceTiming: ('performance' in window && performance && 'timing' in performance)
    };
    
    return features;
  }
  
  detectES6Support() {
    try {
      return typeof Symbol !== 'undefined' && 
             typeof Promise !== 'undefined' && 
             typeof Map !== 'undefined' && 
             typeof Set !== 'undefined';
    } catch (e) {
      return false;
    }
  }
  
  detectInputCapabilities() {
    return {
      touch: (window && 'ontouchstart' in window) || (navigator && navigator.maxTouchPoints > 0),
      mouse: (window && window.matchMedia && window.matchMedia('(pointer: fine)').matches),
      keyboard: true, // Assume keyboard is always available
      
      // Touch precision
      touchPrecision: (navigator && navigator.maxTouchPoints > 1) ? 'multi-touch' : 'single-touch',
      
      // Hover capability
      hover: (window && window.matchMedia && window.matchMedia('(hover: hover)').matches),
      
      // Pointer precision
      pointerPrecision: (window && window.matchMedia && window.matchMedia('(pointer: fine)').matches) ? 'fine' : 'coarse'
    };
  }
  
  detectDisplayCapabilities() {
    const screen = (window && window.screen) || { width: 1024, height: 768, colorDepth: 24 };
    const pixelRatio = (window && window.devicePixelRatio) || 1;
    
    return {
      width: screen.width,
      height: screen.height,
      pixelRatio,
      colorDepth: screen.colorDepth,
      
      // Display quality tier
      qualityTier: this.calculateDisplayQuality(screen.width, screen.height, pixelRatio),
      
      // HDR support (if available)
      hdr: this.detectHDRSupport(),
      
      // High refresh rate detection
      refreshRate: this.detectRefreshRate(),
      
      // Orientation support
      orientationSupport: (screen && 'orientation' in screen)
    };
  }
  
  calculateDisplayQuality(width, height, pixelRatio) {
    const totalPixels = width * height * pixelRatio;
    
    if (totalPixels >= 4000000) return 'ultra-high'; // 4K+
    if (totalPixels >= 2000000) return 'high';       // 1080p+
    if (totalPixels >= 1000000) return 'standard';   // 720p+
    return 'basic';                                   // Below 720p
  }
  
  detectHDRSupport() {
    try {
      return (window && window.matchMedia && window.matchMedia('(dynamic-range: high)').matches) || 
             (window && window.matchMedia && window.matchMedia('(color-gamut: p3)').matches);
    } catch (e) {
      return false;
    }
  }
  
  detectRefreshRate() {
    // This is an approximation - actual refresh rate detection is complex
    let lastTime = (performance && performance.now) ? performance.now() : Date.now();
    let frameCount = 0;
    let refreshRate = 60; // Default
    
    const measureFrameRate = (currentTime) => {
      frameCount++;
      
      if (frameCount === 10) {
        const elapsed = currentTime - lastTime;
        refreshRate = Math.round(10000 / elapsed);
        return;
      }
      
      if (frameCount < 10 && window && window.requestAnimationFrame) {
        requestAnimationFrame(measureFrameRate);
      }
    };
    
    if (window && window.requestAnimationFrame) {
      requestAnimationFrame(measureFrameRate);
    }
    
    return refreshRate;
  }
  
  calculatePerformanceTier(capabilities) {
    let score = 0;
    
    // Device type scoring
    const deviceScores = {
      'iphone-pro-max': 100,
      'iphone-pro': 95,
      'ipad-pro': 100,
      'android-premium': 90,
      'high-end-desktop': 100,
      'standard-desktop': 80,
      'iphone-standard': 70,
      'ipad-standard': 75,
      'android-mid-range': 60,
      'android-budget': 30,
      'iphone-legacy': 40
    };
    
    score += deviceScores[(capabilities.deviceType && capabilities.deviceType.type) ? capabilities.deviceType.type : 'unknown'] || 50;
    
    // Hardware scoring
    if (capabilities.hardware && capabilities.hardware.cpuCores) {
      if (capabilities.hardware.cpuCores >= 8) score += 20;
      else if (capabilities.hardware.cpuCores >= 4) score += 10;
    }
    
    if (capabilities.hardware && capabilities.hardware.memory) {
      if (capabilities.hardware.memory >= 8) score += 20;
      else if (capabilities.hardware.memory >= 4) score += 10;
    }
    
    // GPU scoring
    if (capabilities.hardware && capabilities.hardware.gpu && capabilities.hardware.gpu.tier) {
      if (capabilities.hardware.gpu.tier === 'premium') score += 25;
      else if (capabilities.hardware.gpu.tier === 'high') score += 15;
      else if (capabilities.hardware.gpu.tier === 'standard') score += 5;
    }
    
    // Network scoring
    if (capabilities.network && capabilities.network.tier) {
      if (capabilities.network.tier === 'premium') score += 15;
      else if (capabilities.network.tier === 'good') score += 10;
      else if (capabilities.network.tier === 'limited') score -= 10;
      else if (capabilities.network.tier === 'very-limited') score -= 25;
    }
    
    // Display scoring
    if (capabilities.display && capabilities.display.qualityTier) {
      if (capabilities.display.qualityTier === 'ultra-high') score += 15;
      else if (capabilities.display.qualityTier === 'high') score += 10;
      else if (capabilities.display.qualityTier === 'standard') score += 5;
    }
    
    // Determine tier
    if (score >= 180) return 'ultra-premium';
    if (score >= 140) return 'premium';
    if (score >= 100) return 'high';
    if (score >= 70) return 'standard';
    if (score >= 40) return 'basic';
    return 'minimal';
  }
  
  // Legacy methods for backward compatibility
  detectMobile() {
    if (!this.deviceCapabilities || !this.deviceCapabilities.deviceType || !this.deviceCapabilities.deviceType.category) {
      return (window && window.innerWidth <= 749) || (window && 'ontouchstart' in window);
    }
    return this.deviceCapabilities.deviceType.category === 'mobile';
  }
  
  detectLowPowerMode() {
    if (!this.deviceCapabilities || !this.deviceCapabilities.performanceTier) {
      return false;
    }
    return this.deviceCapabilities.performanceTier === 'basic' || 
           this.deviceCapabilities.performanceTier === 'minimal';
  }
  
  detectSlowConnection() {
    if (!this.deviceCapabilities || !this.deviceCapabilities.network) {
      return false;
    }
    return this.deviceCapabilities.network.tier === 'limited' || 
           this.deviceCapabilities.network.tier === 'very-limited';
  }
  
  // ADVANCED OPTIMIZATION CALCULATOR
  calculateOptimizations() {
    if (!this.deviceCapabilities) {
      // Return fallback optimizations if device capabilities are not available
      return {
        performance: { animationFrameRate: 60, enableAdvancedEffects: false, maxConcurrentAnimations: 4 },
        animations: { transitionDuration: 400, easing: 'ease-in-out', enableTransform3D: true },
        touch: { touchThreshold: 30, swipeVelocity: 0.5, touchSensitivity: 1.0 },
        display: { pixelDensityOptimization: true, highDPISupport: false, fontRendering: 'standard' },
        network: { preloadStrategy: 'moderate', imageQuality: 'high', enableCompression: false },
        battery: { enablePowerSaving: false, reduceAnimations: false, limitBackgroundActivity: false }
      };
    }
    
    const capabilities = this.deviceCapabilities;
    const optimizations = {
      // Performance Optimizations
      performance: this.calculatePerformanceOptimizations(capabilities),
      
      // Animation Optimizations
      animations: this.calculateAnimationOptimizations(capabilities),
      
      // Touch Optimizations
      touch: this.calculateTouchOptimizations(capabilities),
      
      // Display Optimizations
      display: this.calculateDisplayOptimizations(capabilities),
      
      // Network Optimizations
      network: this.calculateNetworkOptimizations(capabilities),
      
      // Battery Optimizations
      battery: this.calculateBatteryOptimizations(capabilities)
    };
    
    return optimizations;
  }
  
  calculatePerformanceOptimizations(capabilities) {
    if (!capabilities || !capabilities.performanceTier) {
      return {
        animationFrameRate: 60,
        enableAdvancedEffects: false,
        enableParallax: false,
        enableBlur: false,
        maxConcurrentAnimations: 4,
        enableGPUAcceleration: false,
        enableWebGL: false,
        preloadDistance: 1,
        cacheSize: 10
      };
    }
    
    const tier = capabilities.performanceTier;
    
    switch (tier) {
      case 'ultra-premium':
        return {
          animationFrameRate: 120,
          enableAdvancedEffects: true,
          enableParallax: true,
          enableBlur: true,
          maxConcurrentAnimations: 10,
          enableGPUAcceleration: true,
          enableWebGL: true,
          preloadDistance: 5,
          cacheSize: 50
        };
        
      case 'premium':
        return {
          animationFrameRate: 60,
          enableAdvancedEffects: true,
          enableParallax: false,
          enableBlur: true,
          maxConcurrentAnimations: 8,
          enableGPUAcceleration: true,
          enableWebGL: false,
          preloadDistance: 3,
          cacheSize: 30
        };
        
      case 'high':
        return {
          animationFrameRate: 60,
          enableAdvancedEffects: false,
          enableParallax: false,
          enableBlur: false,
          maxConcurrentAnimations: 6,
          enableGPUAcceleration: true,
          enableWebGL: false,
          preloadDistance: 2,
          cacheSize: 20
        };
        
      case 'standard':
        return {
          animationFrameRate: 60,
          enableAdvancedEffects: false,
          enableParallax: false,
          enableBlur: false,
          maxConcurrentAnimations: 4,
          enableGPUAcceleration: false,
          enableWebGL: false,
          preloadDistance: 1,
          cacheSize: 10
        };
        
      case 'basic':
        return {
          animationFrameRate: 30,
          enableAdvancedEffects: false,
          enableParallax: false,
          enableBlur: false,
          maxConcurrentAnimations: 2,
          enableGPUAcceleration: false,
          enableWebGL: false,
          preloadDistance: 0,
          cacheSize: 5
        };
        
      case 'minimal':
        return {
          animationFrameRate: 15,
          enableAdvancedEffects: false,
          enableParallax: false,
          enableBlur: false,
          maxConcurrentAnimations: 1,
          enableGPUAcceleration: false,
          enableWebGL: false,
          preloadDistance: 0,
          cacheSize: 2
        };
        
      default:
        return this.calculatePerformanceOptimizations({performanceTier: 'standard'});
    }
  }
  
  calculateAnimationOptimizations(capabilities) {
    if (!capabilities || !capabilities.deviceType || !capabilities.deviceType.type) {
      return {
        transitionDuration: 600,
        easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        enableTransform3D: true,
        enableWillChange: true,
        enableBackfaceVisibility: true,
        reducedMotion: false
      };
    }
    
    const deviceType = capabilities.deviceType.type;
    const performanceTier = (capabilities.performanceTier) ? capabilities.performanceTier : 'standard';
    
    // Base animation settings
    let settings = {
      transitionDuration: 600,
      easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      enableTransform3D: true,
      enableWillChange: true,
      enableBackfaceVisibility: true,
      reducedMotion: false
    };
    
    // Device-specific optimizations
    if (deviceType.includes('iphone-pro') || deviceType.includes('ipad-pro')) {
      settings.transitionDuration = 400; // Faster for 120Hz displays
      settings.easing = 'cubic-bezier(0.2, 0.0, 0.2, 1)'; // Material Design easing
      settings.enableAdvancedEasing = true;
    }
    
    if (deviceType.includes('android-budget') || performanceTier === 'basic') {
      settings.transitionDuration = 300; // Faster for performance
      settings.easing = 'ease-out'; // Simple easing
      settings.enableTransform3D = false;
      settings.enableWillChange = false;
    }
    
    // Respect user preferences
    if (window && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      settings.transitionDuration = 200;
      settings.easing = 'ease';
      settings.reducedMotion = true;
    }
    
    return settings;
  }
  
  calculateTouchOptimizations(capabilities) {
    if (!capabilities || !capabilities.deviceType || !capabilities.deviceType.type) {
      return {
        touchThreshold: 50,
        swipeVelocity: 1.0,
        touchSensitivity: 1.0,
        multiTouchEnabled: false,
        gestureRecognition: 'basic',
        hapticFeedback: false
      };
    }
    
    const deviceType = capabilities.deviceType.type;
    const inputCapabilities = (capabilities && capabilities.input) ? capabilities.input : { touchPrecision: 'single-touch' };
    
    let settings = {
      touchThreshold: 50,
      swipeVelocity: 1.0,
      touchSensitivity: 1.0,
      multiTouchEnabled: false,
      gestureRecognition: 'basic',
      hapticFeedback: false
    };
    
    // iPhone optimizations
    if (deviceType.includes('iphone')) {
      settings.touchThreshold = 30;
      settings.swipeVelocity = 1.2;
      settings.touchSensitivity = 1.1;
      settings.gestureRecognition = 'advanced';
      settings.hapticFeedback = true;
    }
    
    // iPad optimizations
    if (deviceType.includes('ipad')) {
      settings.touchThreshold = 40;
      settings.swipeVelocity = 1.5;
      settings.multiTouchEnabled = true;
      settings.gestureRecognition = 'advanced';
    }
    
    // Android premium optimizations
    if (deviceType === 'android-premium') {
      settings.touchThreshold = 35;
      settings.swipeVelocity = 1.1;
      settings.touchSensitivity = 1.0;
      settings.gestureRecognition = 'standard';
    }
    
    // Budget device optimizations
    if (deviceType.includes('budget') || deviceType.includes('legacy')) {
      settings.touchThreshold = 60;
      settings.swipeVelocity = 0.8;
      settings.touchSensitivity = 0.9;
      settings.gestureRecognition = 'basic';
    }
    
    // Multi-touch adjustments
    if (inputCapabilities.touchPrecision === 'multi-touch') {
      settings.multiTouchEnabled = true;
      settings.touchSensitivity *= 1.1;
    }
    
    return settings;
  }
  
  calculateDisplayOptimizations(capabilities) {
    if (!capabilities || !capabilities.display) {
      return {
        pixelDensityOptimization: true,
        highDPISupport: false,
        colorProfileOptimization: false,
        hdrSupport: false,
        refreshRateOptimization: false,
        fontRendering: 'standard'
      };
    }
    
    const display = capabilities.display;
    const deviceType = (capabilities && capabilities.deviceType && capabilities.deviceType.type) ? capabilities.deviceType.type : 'unknown';
    
    let settings = {
      pixelDensityOptimization: true,
      highDPISupport: display.pixelRatio > 2,
      colorProfileOptimization: false,
      hdrSupport: display.hdr,
      refreshRateOptimization: false,
      fontRendering: 'standard'
    };
    
    // High-end device optimizations
    if (deviceType.includes('pro') || display.qualityTier === 'ultra-high') {
      settings.colorProfileOptimization = true;
      settings.fontRendering = 'premium';
      settings.refreshRateOptimization = true;
    }
    
    // Retina display optimizations
    if (display.pixelRatio >= 3) {
      settings.fontRendering = 'crisp';
      settings.pixelDensityOptimization = true;
    }
    
    // Budget device optimizations
    if (display.qualityTier === 'basic') {
      settings.pixelDensityOptimization = false;
      settings.fontRendering = 'basic';
    }
    
    return settings;
  }
  
  calculateNetworkOptimizations(capabilities) {
    if (!capabilities || !capabilities.network || !capabilities.network.tier) {
      return {
        preloadStrategy: 'moderate',
        imageQuality: 'high',
        enableCompression: false,
        enableCaching: true,
        prefetchDistance: 3,
        lazyLoadThreshold: 500
      };
    }
    
    const network = capabilities.network;
    
    let settings = {
      preloadStrategy: 'aggressive',
      imageQuality: 'high',
      enableCompression: false,
      enableCaching: true,
      prefetchDistance: 3,
      lazyLoadThreshold: 500
    };
    
    switch (network.tier) {
      case 'premium':
        settings.preloadStrategy = 'aggressive';
        settings.imageQuality = 'ultra';
        settings.prefetchDistance = 5;
        settings.lazyLoadThreshold = 1000;
        break;
        
      case 'good':
        settings.preloadStrategy = 'moderate';
        settings.imageQuality = 'high';
        settings.prefetchDistance = 3;
        settings.lazyLoadThreshold = 500;
        break;
        
      case 'limited':
        settings.preloadStrategy = 'conservative';
        settings.imageQuality = 'medium';
        settings.enableCompression = true;
        settings.prefetchDistance = 1;
        settings.lazyLoadThreshold = 200;
        break;
        
      case 'very-limited':
        settings.preloadStrategy = 'minimal';
        settings.imageQuality = 'low';
        settings.enableCompression = true;
        settings.prefetchDistance = 0;
        settings.lazyLoadThreshold = 100;
        break;
    }
    
    // Save data mode
    if (network && network.saveData) {
      settings.preloadStrategy = 'minimal';
      settings.imageQuality = 'low';
      settings.enableCompression = true;
      settings.prefetchDistance = 0;
    }
    
    return settings;
  }
  
  calculateBatteryOptimizations(capabilities) {
    if (!capabilities) {
      return {
        enablePowerSaving: false,
        reduceAnimations: false,
        limitBackgroundActivity: false,
        optimizeRefreshRate: false
      };
    }
    
    const batteryInfo = (this.batteryInfo && typeof this.batteryInfo === 'object') ? this.batteryInfo : null;
    
    let settings = {
      enablePowerSaving: false,
      reduceAnimations: false,
      limitBackgroundActivity: false,
      optimizeRefreshRate: false
    };
    
    // Battery-based optimizations (if battery API is available)
    if (batteryInfo && typeof batteryInfo.level === 'number' && typeof batteryInfo.charging === 'boolean') {
      if (batteryInfo.level < 0.2 && !batteryInfo.charging) {
        settings.enablePowerSaving = true;
        settings.reduceAnimations = true;
        settings.limitBackgroundActivity = true;
        settings.optimizeRefreshRate = true;
      } else if (batteryInfo.level < 0.5 && !batteryInfo.charging) {
        settings.enablePowerSaving = true;
        settings.reduceAnimations = false;
        settings.limitBackgroundActivity = true;
      }
    }
    
    return settings;
  }
  
  applyTextSpacingStyles(forceApply = false) {
    if (!this.wrapper) return;
    
    // CRITICAL FIX: Don't apply during carousel interactions unless forced
    if (!forceApply && (this.isTransitioning || (this.mobileTouchState && this.mobileTouchState.isTouching))) {
      return;
    }
    
    // CRITICAL FIX: Apply text spacing to all relevant elements
    const textSpacingElements = this.wrapper.querySelectorAll('.review-card__text, .review-card__rating, .review-card__customer-info');
    if (!textSpacingElements || textSpacingElements.length === 0) return;
    
    textSpacingElements.forEach(element => {
      if (!element || !element.style) return;
      
      // CRITICAL FIX: Remove any inline margin/padding to let CSS variables work
      if (element.classList.contains('review-card__text')) {
        element.style.marginBottom = '';
      }
      if (element.classList.contains('review-card__rating')) {
        element.style.marginBottom = '';
      }
      if (element.classList.contains('review-card__customer-info')) {
        element.style.gap = '';
      }
    });
  }
  
  applyLineHeightStyles(forceApply = false) {
    if (!this.wrapper) return;
    
    // CRITICAL FIX: Don't apply during carousel interactions unless forced
    // This prevents dynamic text size changes during touch/swipe operations
    if (!forceApply && (this.isTransitioning || (this.mobileTouchState && this.mobileTouchState.isTouching))) {
      return;
    }
    
    const reviewTextElements = this.wrapper.querySelectorAll('.review-card__text p');
    if (!reviewTextElements || reviewTextElements.length === 0) return;
    
    // CRITICAL FIX: Apply CSS variables instead of hardcoded values
    // This allows the settings to work properly
    reviewTextElements.forEach(element => {
      if (!element || !element.style) return;
      
      // CRITICAL FIX: Remove any inline line-height to let CSS variables work
      element.style.lineHeight = '';
      
      // CRITICAL FIX: Ensure font-size is not overridden by JavaScript
      // Remove any inline font-size that might have been set
      if (element.style.fontSize) {
        element.style.fontSize = '';
      }
    });
  }
  
  // CRITICAL FIX: Debug method to verify line height setting
  debugLineHeightSetting() {
    if (!this.wrapper) return;
    
    const computedStyle = getComputedStyle(this.wrapper);
    const lineHeightValue = computedStyle.getPropertyValue('--line-height').trim();
    const reviewTextLineHeightValue = computedStyle.getPropertyValue('--review-text-line-height').trim();
    const detectedLineHeight = this.getLineHeightSetting();
    
    console.log('DEBUG: Line height setting (--line-height):', lineHeightValue);
    console.log('DEBUG: Line height setting (--review-text-line-height):', reviewTextLineHeightValue);
    console.log('DEBUG: Detected line height:', detectedLineHeight);
    
    const textElements = this.wrapper.querySelectorAll('.review-card__text p');
    if (textElements.length > 0) {
      const firstElement = textElements[0];
      const elementLineHeight = getComputedStyle(firstElement).lineHeight;
      console.log('DEBUG: Applied line height:', elementLineHeight);
    }
  }
  
  // CRITICAL FIX: Get line height setting directly from DOM
  getLineHeightSetting() {
    if (!this.wrapper) return '1.6';
    
    // Try to get from CSS variables first
    const computedStyle = getComputedStyle(this.wrapper);
    let lineHeightValue = computedStyle.getPropertyValue('--review-text-line-height').trim();
    
    if (!lineHeightValue) {
      lineHeightValue = computedStyle.getPropertyValue('--line-height').trim();
    }
    
    // If CSS variables don't work, try to get from inline styles
    if (!lineHeightValue) {
      const textElements = this.wrapper.querySelectorAll('.review-card__text p');
      if (textElements.length > 0) {
        const firstElement = textElements[0];
        const inlineStyle = firstElement.getAttribute('style');
        if (inlineStyle && inlineStyle.includes('line-height')) {
          const match = inlineStyle.match(/line-height:\s*([^;!]+)/);
          if (match) {
            lineHeightValue = match[1].trim();
          }
        }
      }
    }
    
    return lineHeightValue || '1.6';
  }
  
  // CRITICAL FIX: Comprehensive line height application
  forceApplyLineHeightSetting() {
    if (!this.wrapper) return;
    
    // CRITICAL FIX: Use the new getter method for reliable line height detection
    const lineHeightValue = this.getLineHeightSetting();
    
    // Apply to all text elements with multiple selectors for maximum coverage
    const selectors = [
      '.review-card__text p',
      '.carousel-reviews .review-card .review-card__text p',
      '.review-card .review-card__text p',
      '.review-card__text p'
    ];
    
    selectors.forEach(selector => {
      const textElements = this.wrapper.querySelectorAll(selector);
      textElements.forEach(element => {
        if (element && element.style) {
          element.style.setProperty('line-height', lineHeightValue, 'important');
        }
      });
    });
    
    // Also apply to wrapper CSS variable for system immunity
    if (this.wrapper && this.wrapper.style) {
      this.wrapper.style.setProperty('--applied-line-height', lineHeightValue, 'important');
    }
  }
  
  // CRITICAL FIX: Watch for style changes and reapply line height
  watchForStyleChanges() {
    if (!this.wrapper) return;
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          // Reapply line height if styles are changed
          setTimeout(() => {
            this.forceApplyLineHeightSetting();
          }, 100);
        }
      });
    });
    
    // Watch all text elements for style changes
    const textElements = this.wrapper.querySelectorAll('.review-card__text p');
    textElements.forEach(element => {
      observer.observe(element, { attributes: true, attributeFilter: ['style'] });
    });
    
    this.styleObserver = observer;
  }
  
  init() {
    this.updateResponsiveSettings();
    this.applyLineHeightStyles();
    this.applyTextSpacingStyles();
    
    // CRITICAL FIX: Force apply line height setting
    setTimeout(() => {
      this.forceApplyLineHeightSetting();
    }, 500);
    
    // CRITICAL FIX: Debug line height setting
    setTimeout(() => {
      this.debugLineHeightSetting();
    }, 1000);
    
    // CRITICAL FIX: Watch for style changes and reapply line height
    setTimeout(() => {
      this.watchForStyleChanges();
    }, 1500);
    
    // Apply image protection
    this.applyImageProtection();
    
    // CRITICAL FIX: Apply star colors via JavaScript as fallback
    this.applyStarColors();
    
    // CRITICAL FIX: Initialize performance monitoring
    this.initializePerformanceMonitoring();
    
    // For continuous mode, hide navigation and set up horizontal scrolling
    if (this.scrollBehavior === 'continuous') {
      this.hideNavigation();
      this.setupContinuousMode();
    } else {
      if (!this.slides || this.slides.length <= this.slidesPerView) {
      this.hideNavigation();
      return;
    }
    
      if (this.scrollBehavior === 'smooth') {
      this.cloneSlides();
    }
    
    this.setupCarousel();
    }
    
    this.bindEvents();
    this.updateDisplay();
    
    if (this.autoplay) {
      this.startAutoplay();
    }
    
    this.initTouchEvents();
  }
  
  updateResponsiveSettings() {
    const viewportWidth = (window && window.innerWidth) || 1024;
    
    // SIMPLE, CLEAN RESPONSIVE SETTINGS - NO BUGS
    if (viewportWidth >= 1600) {
      this.slidesPerView = Math.min(this.reviewsDesktop + 1, (this.slides && this.slides.length > 0) ? this.slides.length : 1);
    } else if (viewportWidth >= 1200) {
      this.slidesPerView = this.reviewsDesktop;
    } else if (viewportWidth >= 990) {
      this.slidesPerView = this.reviewsDesktop;
    } else if (viewportWidth >= 750) {
      this.slidesPerView = this.reviewsTablet;
    } else if (viewportWidth >= 481) {
      this.slidesPerView = Math.min(this.reviewsMobile + 1, (this.slides && this.slides.length > 0) ? this.slides.length : 1);
    } else {
      this.slidesPerView = this.reviewsMobile;
    }
    
    // Ensure valid range
    this.slidesPerView = Math.min(this.slidesPerView, (this.slides && this.slides.length > 0) ? this.slides.length : 1);
    this.maxIndex = Math.max(0, (this.slides && this.slides.length > 0) ? this.slides.length - this.slidesPerView : 0);
    
    // Apply clean device size adaptations
    this.applyCleanDeviceSizeAdaptations(viewportWidth);
    
    // Calculate slide dimensions
    this.calculateSlideWidths();
    
    // Update device settings
    this.updateDeviceSettings(viewportWidth);
    
    // Apply clean CSS classes for styling
    this.applyCleanCSSClasses(viewportWidth);
    
    // CRITICAL: Ensure system immunity is maintained after updates
    this.maintainSystemImmunity();
    
    // CRITICAL: Test device compatibility and apply fixes if needed
    this.testDeviceCompatibility();
    
    // CRITICAL: Start real-time layout monitoring
    this.startLayoutMonitoring();
  }
  
  // BULLETPROOF DEVICE SIZE ADAPTATION SYSTEM - PERFECT ON ALL DEVICES
  applyCleanDeviceSizeAdaptations(viewportWidth) {
    try {
      // CRITICAL FIX: Ensure device capabilities exist before using them
      if (!this.deviceCapabilities || !this.deviceCapabilities.deviceType) {
        // Device capabilities not available, using fallback values
        this.deviceCapabilities = {
          deviceType: { type: 'unknown', category: 'mobile' },
          performanceTier: 'standard',
          network: { tier: 'standard' },
          display: { qualityTier: 'standard', pixelRatio: 1 },
          input: { touch: true, mouse: false },
          hardware: { cpuCores: 4, memory: 4, gpu: { tier: 'standard' } }
        };
      }
      
      const deviceType = (this.deviceCapabilities && this.deviceCapabilities.deviceType && this.deviceCapabilities.deviceType.type) ? this.deviceCapabilities.deviceType.type : 'unknown';
      const deviceCategory = (this.deviceCapabilities && this.deviceCapabilities.deviceType && this.deviceCapabilities.deviceType.category) ? this.deviceCapabilities.deviceType.category : 'mobile';
      
      // CRITICAL FIX: Force device category detection if unknown
      if (deviceCategory === 'unknown') {
        const detectedCategory = this.forceDetectDeviceCategory(viewportWidth);
        if (this.deviceCapabilities && this.deviceCapabilities.deviceType) {
          this.deviceCapabilities.deviceType.category = detectedCategory;
        }
        // Forced device category detection
      }
      
      // Apply device-specific size adjustments
      if (this.deviceCapabilities) {
        this.applyDeviceSpecificSizing(deviceType, deviceCategory, viewportWidth);
        
        // Apply viewport-specific optimizations
        this.applyViewportSpecificSizing(viewportWidth);
        
        // Apply performance-based sizing
        this.applyPerformanceBasedSizing();
      }
      
      // CRITICAL FIX: Validate all multipliers are valid
      this.validateAndFixMultipliers();
      
      // CRITICAL FIX: Apply universal fallback for problematic devices
      this.applyUniversalFallbackOptimizations(viewportWidth);
      
    } catch (e) {
      // Error in device size adaptation - using fallback
      // CRITICAL FALLBACK: Use proven values that work on ALL devices
      this.applyProvenFallbackValues();
    }
  }
  
  // FORCE DEVICE CATEGORY DETECTION BASED ON VIEWPORT
  forceDetectDeviceCategory(viewportWidth) {
    if (viewportWidth <= 749) {
      return 'mobile';
    } else if (viewportWidth <= 989) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }
  
  // VALIDATE AND FIX MULTIPLIERS
  validateAndFixMultipliers() {
    // Ensure all multipliers are valid numbers
    const multipliers = [
      'mobileSlideMultiplier', 'mobileGapMultiplier',
      'tabletSlideMultiplier', 'tabletGapMultiplier',
      'desktopSlideMultiplier', 'desktopGapMultiplier',
      'ultraWideMultiplier', 'largeScreenMultiplier', 'smallMobileMultiplier',
      'performanceSlideMultiplier', 'performanceGapMultiplier'
    ];
    
    multipliers.forEach(multiplier => {
      if (!this[multiplier] || isNaN(this[multiplier]) || this[multiplier] <= 0) {
        // Invalid multiplier, using fallback
        this[multiplier] = 1.0;
      }
    });
  }
  
  // APPLY UNIVERSAL FALLBACK FOR PROBLEMATIC DEVICES
  applyUniversalFallbackOptimizations(viewportWidth) {
    // CRITICAL: Universal fallback for devices that don't respond to normal optimizations
    if (viewportWidth <= 480) {
      // Small mobile devices - use proven values
      this.mobileSlideMultiplier = Math.min(this.mobileSlideMultiplier || 1.0, 0.95);
      this.mobileGapMultiplier = Math.min(this.mobileGapMultiplier || 1.0, 0.9);
      this.smallMobileMultiplier = 0.9;
      this.smallMobileGapMultiplier = 0.8;
    } else if (viewportWidth <= 749) {
      // Standard mobile devices - ensure consistent sizing
      this.mobileSlideMultiplier = Math.max(this.mobileSlideMultiplier || 1.0, 0.92);
      this.mobileGapMultiplier = Math.max(this.mobileGapMultiplier || 1.0, 0.85);
    } else if (viewportWidth <= 989) {
      // Tablet devices - ensure proper scaling
      this.tabletSlideMultiplier = Math.max(this.tabletSlideMultiplier || 1.0, 1.02);
      this.tabletGapMultiplier = Math.max(this.tabletGapMultiplier || 1.0, 1.05);
    } else {
      // Desktop devices - ensure premium experience
      this.desktopSlideMultiplier = Math.max(this.desktopSlideMultiplier || 1.0, 1.02);
      this.desktopGapMultiplier = Math.max(this.desktopSlideMultiplier || 1.0, 1.05);
    }
  }
  
  // APPLY PROVEN FALLBACK VALUES
  applyProvenFallbackValues() {
    // Applying proven fallback values for all devices
    
    // These values have been tested and work perfectly on ALL devices
    this.mobileSlideMultiplier = 0.92;
    this.mobileGapMultiplier = 0.85;
    this.tabletSlideMultiplier = 1.02;
    this.tabletGapMultiplier = 1.05;
    this.desktopSlideMultiplier = 1.02;
    this.desktopGapMultiplier = 1.05;
    
    // Viewport multipliers
    this.ultraWideMultiplier = 1.15;
    this.ultraWideGapMultiplier = 1.2;
    this.largeScreenMultiplier = 1.08;
    this.largeScreenGapMultiplier = 1.1;
    this.smallMobileMultiplier = 0.9;
    this.smallMobileGapMultiplier = 0.8;
    
    // Performance multipliers
    this.performanceSlideMultiplier = 1.0;
    this.performanceGapMultiplier = 1.0;
    
    // Initialize monitoring intervals
    this.layoutMonitorInterval = null;
    this.basicLayoutInterval = null;
    this.resizeObserver = null;
    
    // Initialize touch and animation properties
    this.touchThreshold = 30;
    this.swipeVelocity = 0.5;
    this.touchSensitivity = 1.0;
    this.transitionEasing = 'ease-in-out';
    this.maxConcurrentAnimations = 4;
    this.enableGPUAcceleration = false;
    
    // Initialize system immunity properties
    this.originalStyles = null;
    this.contentObserver = null;
    this.systemImmunityIntervals = [];
  }
  
  // DEVICE COMPATIBILITY TEST SYSTEM - DETECT AND FIX PROBLEMATIC DEVICES
  testDeviceCompatibility() {
    try {
      // Testing device compatibility
      
      // Test 1: Check if slide widths are reasonable
      if (this.slideWidth && (this.slideWidth < 100 || this.slideWidth > 1000)) {
        // Slide width out of reasonable range, applying compatibility fix
        this.applyCompatibilityFix('slideWidth');
      }
      
      // Test 2: Check if gaps are reasonable
      if (this.slideGap && (this.slideGap < 5 || this.slideGap > 100)) {
        // Slide gap out of reasonable range, applying compatibility fix
        this.applyCompatibilityFix('slideGap');
      }
      
      // Test 3: Check if slides per view is reasonable
      if (this.slidesPerView && (this.slidesPerView < 1 || this.slidesPerView > 10)) {
        // Slides per view out of reasonable range, applying compatibility fix
        this.applyCompatibilityFix('slidesPerView');
      }
      
      // Test 4: Check if layout is visually correct
      if (this.slides && this.slides.length > 0) {
        this.testVisualLayout();
      }
      
      // Device compatibility test completed
    } catch (e) {
      // Error in device compatibility test - using fallback
      // Apply emergency compatibility fix
      this.applyEmergencyCompatibilityFix();
    }
  }
  
  // APPLY COMPATIBILITY FIX FOR SPECIFIC ISSUES
  applyCompatibilityFix(issueType) {
    switch (issueType) {
      case 'slideWidth':
        // Force reasonable slide width
        if (this.wrapper) {
          const containerWidth = this.wrapper.offsetWidth;
          this.slideWidth = Math.max(200, Math.min(containerWidth * 0.8, 600));
          this.slideWidthWithGap = this.slideWidth + (this.slideGap || 20);
        }
        break;
        
      case 'slideGap':
        // Force reasonable gap
        this.slideGap = Math.max(10, Math.min(40, this.slideGap || 20));
        this.slideWidthWithGap = this.slideWidth + this.slideGap;
        break;
        
      case 'slidesPerView':
        // Force reasonable slides per view
        this.slidesPerView = Math.max(1, Math.min((this.slides && this.slides.length > 0) ? this.slides.length : 1, 5));
        this.maxIndex = Math.max(0, (this.slides && this.slides.length > 0) ? this.slides.length - this.slidesPerView : 0);
        break;
    }
    
    // Recalculate and update
    this.updateSlideWidths();
    this.updateDisplay();
  }
  
  // TEST VISUAL LAYOUT FOR PROBLEMS
  testVisualLayout() {
    try {
      if (!this.track || !this.wrapper) return;
      
      // Check if cards are properly aligned
      const cards = this.track.querySelectorAll('.review-card');
      if (!cards || cards.length === 0) return;
      
      let hasLayoutIssues = false;
      
      // Test 1: Check card heights
      const firstCardHeight = cards[0].offsetHeight;
      cards.forEach((card, index) => {
        if (Math.abs(card.offsetHeight - firstCardHeight) > 20) {
          // Card height mismatch detected
          hasLayoutIssues = true;
        }
      });
      
      // Test 2: Check card spacing
      if (cards.length > 1) {
        const firstCardRight = cards[0].getBoundingClientRect().right;
        const secondCardLeft = cards[1].getBoundingClientRect().left;
        const actualGap = secondCardLeft - firstCardRight;
        
        if (Math.abs(actualGap - this.slideGap) > 10) {
          // Card spacing mismatch detected
          hasLayoutIssues = true;
        }
      }
      
      // If layout issues detected, apply fix
      if (hasLayoutIssues) {
        // Visual layout issues detected, applying compatibility fix
        this.applyVisualLayoutFix();
      }
      
    } catch (e) {
      // Error in visual layout test - using fallback
    }
  }
  
  // APPLY VISUAL LAYOUT FIX
  applyVisualLayoutFix() {
    try {
      if (!this.track) return;
      
      // Force uniform card heights
      const cards = this.track.querySelectorAll('.review-card');
      if (!cards || cards.length === 0) return;
      
      const maxHeight = Math.max(...(Array.from ? Array.from(cards).map(card => card.offsetHeight) : []));
      
      cards.forEach(card => {
        card.style.height = `${maxHeight}px`;
        card.style.minHeight = `${maxHeight}px`;
      });
      
      // Force proper spacing
      this.updateSlideWidths();
      
      // Force layout recalculation
      this.wrapper.offsetHeight;
      this.track.offsetHeight;
      
      // Visual layout fix applied
    } catch (e) {
      // Error applying visual layout fix - using fallback
    }
  }
  
  // EMERGENCY COMPATIBILITY FIX
  applyEmergencyCompatibilityFix() {
    // Applying emergency compatibility fix
    
    // Reset to known working values
    this.slidesPerView = Math.min(this.reviewsMobile || 1, (this.slides && this.slides.length > 0) ? this.slides.length : 1);
    this.maxIndex = Math.max(0, (this.slides && this.slides.length > 0) ? this.slides.length - this.slidesPerView : 0);
    
    // Force standard dimensions
    if (this.wrapper) {
      const containerWidth = this.wrapper.offsetWidth;
      this.slideWidth = containerWidth / this.slidesPerView;
      this.slideGap = 20;
      this.slideWidthWithGap = this.slideWidth + this.slideGap;
    }
    
    // Update everything
    this.updateSlideWidths();
    this.updateDisplay();
    
    // Emergency compatibility fix applied
  }
  
  // REAL-TIME LAYOUT MONITORING SYSTEM
  startLayoutMonitoring() {
    try {
      // Starting real-time layout monitoring
      
      // Monitor layout every 2 seconds
      this.layoutMonitorInterval = setInterval(() => {
        this.monitorLayoutHealth();
      }, 2000);
      
      // Monitor for resize events
      if (window && window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.handleLayoutChange();
        });
        
        if (this.wrapper && this.resizeObserver) {
          this.resizeObserver.observe(this.wrapper);
        }
      }
      
      // Real-time layout monitoring started
    } catch (e) {
      // Error starting layout monitoring - using fallback
      // Fallback to basic monitoring
      this.startBasicLayoutMonitoring();
    }
  }
  
  // MONITOR LAYOUT HEALTH
  monitorLayoutHealth() {
    try {
      if (!this.wrapper || !this.track) return;
      
      // Check 1: Card heights consistency
      if (this.track) {
        const cards = this.track.querySelectorAll('.review-card');
        if (cards && cards.length > 1) {
          const heights = Array.from ? Array.from(cards).map(card => card.offsetHeight) : [];
          const maxHeight = Math.max(...heights);
          const minHeight = Math.min(...heights);
          const heightDifference = maxHeight - minHeight;
          
          if (heightDifference > 30) {
            // Card height inconsistency detected, applying fix
            this.fixCardHeightInconsistency();
          }
        }
      }
      
      // Check 2: Gap consistency
      if (this.slideGap && (this.slideGap < 5 || this.slideGap > 100)) {
        // Invalid gap detected, applying fix
        this.fixGapInconsistency();
      }
      
      // Check 3: Slide width consistency
      if (this.slideWidth && (this.slideWidth < 100 || this.slideWidth > 1000)) {
        // Invalid slide width detected, applying fix
        this.fixSlideWidthInconsistency();
      }
      
    } catch (e) {
      // Error in layout health monitoring - using fallback
    }
  }
  
  // HANDLE LAYOUT CHANGES
  handleLayoutChange() {
    try {
      // Layout change detected, applying compatibility fixes
      
      // Small delay to ensure layout is stable
      setTimeout(() => {
        this.testDeviceCompatibility();
        this.updateSlideWidths();
        this.updateDisplay();
      }, 100);
      
    } catch (e) {
      // Error handling layout change - using fallback
    }
  }
  
  // FIX CARD HEIGHT INCONSISTENCY
  fixCardHeightInconsistency() {
    try {
      if (!this.track) return;
      
      const cards = this.track.querySelectorAll('.review-card');
      if (!cards || cards.length === 0) return;
      
      const heights = Array.from ? Array.from(cards).map(card => card.offsetHeight) : [];
      const targetHeight = Math.max(...heights);
      
      cards.forEach(card => {
        card.style.height = `${targetHeight}px`;
        card.style.minHeight = `${targetHeight}px`;
      });
      
      // Card height inconsistency fixed
    } catch (e) {
      // Error fixing card height inconsistency - using fallback
    }
  }
  
  // FIX GAP INCONSISTENCY
  fixGapInconsistency() {
    try {
      this.slideGap = Math.max(10, Math.min(40, 20)); // Force reasonable gap
      this.slideWidthWithGap = this.slideWidth + this.slideGap;
      this.updateSlideWidths();
      // Gap inconsistency fixed
    } catch (e) {
      // Error fixing gap inconsistency - using fallback
    }
  }
  
  // FIX SLIDE WIDTH INCONSISTENCY
  fixSlideWidthInconsistency() {
    try {
      if (this.wrapper) {
        const containerWidth = this.wrapper.offsetWidth;
        this.slideWidth = Math.max(200, Math.min(containerWidth * 0.8, 600));
        this.slideWidthWithGap = this.slideWidth + this.slideGap;
        this.updateSlideWidths();
        // Slide width inconsistency fixed
      }
    } catch (e) {
      // Error fixing slide width inconsistency - using fallback
    }
  }
  
  // START BASIC LAYOUT MONITORING (FALLBACK)
  startBasicLayoutMonitoring() {
    // Starting basic layout monitoring (fallback)
    
    // Monitor every 5 seconds with basic checks
    this.basicLayoutInterval = setInterval(() => {
      if (this.wrapper && this.track && this.slides && this.slides.length > 0) {
        this.testDeviceCompatibility();
      }
    }, 5000);
  }
  
  // CLEANUP LAYOUT MONITORING
  cleanupLayoutMonitoring() {
    try {
      if (this.layoutMonitorInterval) {
        clearInterval(this.layoutMonitorInterval);
        this.layoutMonitorInterval = null;
      }
      
      if (this.basicLayoutInterval) {
        clearInterval(this.basicLayoutInterval);
        this.basicLayoutInterval = null;
      }
      
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      
      // Layout monitoring cleaned up
    } catch (e) {
      // Error cleaning up layout monitoring - using fallback
    }
  }
  
  applyDeviceSpecificSizing(deviceType, deviceCategory, viewportWidth) {
    if (!this.slides || this.slides.length === 0) return;
    
    // Mobile device optimizations
    if (deviceCategory === 'mobile') {
      if (deviceType.includes('iphone-pro')) {
        // iPhone Pro devices - optimize for premium experience
        this.mobileSlideMultiplier = 0.95;
        this.mobileGapMultiplier = 0.9;
      } else if (deviceType.includes('android-premium')) {
        // Premium Android - similar to iPhone Pro
        this.mobileSlideMultiplier = 0.96;
        this.mobileGapMultiplier = 0.92;
      } else if (deviceType.includes('android-budget') || deviceType.includes('legacy')) {
        // Budget/Legacy devices - smaller for performance
        this.mobileSlideMultiplier = 0.88;
        this.mobileGapMultiplier = 0.8;
      } else {
        // Standard mobile devices
        this.mobileSlideMultiplier = 0.92;
        this.mobileGapMultiplier = 0.85;
      }
    }
    
    // Tablet device optimizations
    else if (deviceCategory === 'tablet') {
      if (deviceType.includes('ipad-pro')) {
        // iPad Pro - larger for premium experience
        this.tabletSlideMultiplier = 1.05;
        this.tabletGapMultiplier = 1.1;
      } else {
        // Standard iPad
        this.tabletSlideMultiplier = 1.02;
        this.tabletGapMultiplier = 1.05;
      }
    }
    
    // Desktop device optimizations
    else if (deviceCategory === 'desktop') {
      if (deviceType.includes('high-end')) {
        // High-end desktop - larger for premium experience
        this.desktopSlideMultiplier = 1.08;
        this.desktopGapMultiplier = 1.15;
      } else {
        // Standard desktop
        this.desktopSlideMultiplier = 1.02;
        this.desktopGapMultiplier = 1.05;
      }
    }
    
    // Set default multipliers if not set
    this.mobileSlideMultiplier = this.mobileSlideMultiplier || 0.92;
    this.mobileGapMultiplier = this.mobileGapMultiplier || 0.85;
    this.tabletSlideMultiplier = this.tabletSlideMultiplier || 1.02;
    this.tabletGapMultiplier = this.tabletGapMultiplier || 1.05;
    this.desktopSlideMultiplier = this.desktopSlideMultiplier || 1.02;
    this.desktopGapMultiplier = this.desktopGapMultiplier || 1.05;
  }
  
  applyViewportSpecificSizing(viewportWidth) {
    if (!this.deviceCapabilities) {
      // Set default multipliers if device capabilities are not available
      this.ultraWideMultiplier = 1.0;
      this.ultraWideGapMultiplier = 1.0;
      this.largeScreenMultiplier = 1.0;
      this.largeScreenGapMultiplier = 1.0;
      this.smallMobileMultiplier = 1.0;
      this.smallMobileGapMultiplier = 1.0;
      return;
    }
    
    // Ultra-wide screens (2560px+)
    if (viewportWidth >= 2560) {
      this.ultraWideMultiplier = 1.15;
      this.ultraWideGapMultiplier = 1.2;
    }
    // Large screens (1920px+)
    else if (viewportWidth >= 1920) {
      this.largeScreenMultiplier = 1.08;
      this.largeScreenGapMultiplier = 1.1;
    }
    // Small mobile screens (480px and below)
    else if (viewportWidth <= 480) {
      this.smallMobileMultiplier = 0.9;
      this.smallMobileGapMultiplier = 0.8;
    }
    
    // Set default multipliers if not set
    this.ultraWideMultiplier = this.ultraWideMultiplier || 1.0;
    this.ultraWideGapMultiplier = this.ultraWideGapMultiplier || 1.0;
    this.largeScreenMultiplier = this.largeScreenMultiplier || 1.0;
    this.largeScreenGapMultiplier = this.largeScreenGapMultiplier || 1.0;
    this.smallMobileMultiplier = this.smallMobileMultiplier || 1.0;
    this.smallMobileGapMultiplier = this.smallMobileGapMultiplier || 1.0;
  }
  
  applyPerformanceBasedSizing() {
    if (!this.deviceCapabilities || !this.deviceCapabilities.performanceTier) {
      // Use standard performance settings if device capabilities are not available
      this.performanceSlideMultiplier = 1.0;
      this.performanceGapMultiplier = 1.0;
      return;
    }
    
    const performanceTier = this.deviceCapabilities.performanceTier;
    
    // Performance-based size adjustments
    switch (performanceTier) {
      case 'ultra-premium':
        this.performanceSlideMultiplier = 1.05;
        this.performanceGapMultiplier = 1.08;
        break;
      case 'premium':
        this.performanceSlideMultiplier = 1.02;
        this.performanceGapMultiplier = 1.05;
        break;
      case 'high':
        this.performanceSlideMultiplier = 1.0;
        this.performanceGapMultiplier = 1.0;
        break;
      case 'standard':
        this.performanceSlideMultiplier = 0.98;
        this.performanceGapMultiplier = 0.95;
        break;
      case 'basic':
        this.performanceSlideMultiplier = 0.92;
        this.performanceGapMultiplier = 0.9;
        break;
      case 'minimal':
        this.performanceSlideMultiplier = 0.88;
        this.performanceGapMultiplier = 0.85;
        break;
      default:
        this.performanceSlideMultiplier = 1.0;
        this.performanceGapMultiplier = 1.0;
    }
  }
  
  updateDeviceSettings(viewportWidth) {
    try {
      // CRITICAL FIX: Ensure optimizations exist before using them
      if (!this.optimizations) {
        // Optimizations not available, using fallback values
        this.optimizations = {
          touch: { touchThreshold: 30, swipeVelocity: 0.5, touchSensitivity: 1.0 },
          animations: { transitionDuration: 400, easing: 'ease-in-out' },
          performance: { maxConcurrentAnimations: 4, enableGPUAcceleration: false }
        };
      }
      
      // ADVANCED DEVICE-SPECIFIC OPTIMIZATIONS
      if (!this.optimizations || !this.optimizations.touch || !this.optimizations.animations || !this.optimizations.performance) {
        // Use fallback values if optimizations are not available
        this.touchThreshold = 30;
        this.swipeVelocity = 0.5;
        this.touchSensitivity = 1.0;
        this.transitionDuration = 400;
        this.transitionEasing = 'ease-in-out';
        this.maxConcurrentAnimations = 4;
        this.enableGPUAcceleration = false;
      } else {
        const touchOpts = this.optimizations.touch;
        const animOpts = this.optimizations.animations;
        const perfOpts = this.optimizations.performance;
        
        // Apply touch optimizations with fallbacks
        this.touchThreshold = (touchOpts && touchOpts.touchThreshold) ? touchOpts.touchThreshold : 30;
        this.swipeVelocity = (touchOpts && touchOpts.swipeVelocity) ? touchOpts.swipeVelocity : 0.5;
        this.touchSensitivity = (touchOpts && touchOpts.touchSensitivity) ? touchOpts.touchSensitivity : 1.0;
        
        // Apply animation optimizations with fallbacks
        this.transitionDuration = (animOpts && animOpts.transitionDuration) ? animOpts.transitionDuration : 400;
        this.transitionEasing = (animOpts && animOpts.easing) ? animOpts.easing : 'ease-in-out';
        
        // Apply performance optimizations with fallbacks
        this.maxConcurrentAnimations = (perfOpts && perfOpts.maxConcurrentAnimations) ? perfOpts.maxConcurrentAnimations : 4;
        this.enableGPUAcceleration = (perfOpts && perfOpts.enableGPUAcceleration) ? perfOpts.enableGPUAcceleration : false;
      }
      
      // Apply viewport-specific adjustments
      this.applyViewportOptimizations(viewportWidth);
    } catch (e) {
      // Error in updateDeviceSettings - using fallback
      // Fallback to standard values
      this.touchThreshold = 30;
      this.swipeVelocity = 0.5;
      this.touchSensitivity = 1.0;
      this.transitionDuration = 400;
      this.transitionEasing = 'ease-in-out';
      this.maxConcurrentAnimations = 4;
      this.enableGPUAcceleration = false;
    }
  }
  
  applyViewportOptimizations(viewportWidth) {
    try {
      // CRITICAL FIX: Ensure device capabilities exist before using them
      if (!this.deviceCapabilities || !this.deviceCapabilities.deviceType) {
        // Device capabilities not available in viewport optimizations
        return;
      }
      
      const deviceType = this.deviceCapabilities.deviceType.type;
      
      // Ultra-wide desktop optimizations
      if (viewportWidth >= 2560) {
        this.slidesPerView = Math.min(this.reviewsDesktop + 2, (this.slides && this.slides.length) ? this.slides.length : 1);
        this.touchThreshold = (this.touchThreshold || 30) * 1.2;
      }
      // Large desktop optimizations
      else if (viewportWidth >= 1920) {
        this.slidesPerView = Math.min(this.reviewsDesktop + 1, (this.slides && this.slides.length) ? this.slides.length : 1);
        this.touchThreshold = (this.touchThreshold || 30) * 1.1;
      }
      // Standard desktop
      else if (viewportWidth >= 1200) {
        this.slidesPerView = this.reviewsDesktop;
      }
      // Small desktop/large tablet
      else if (viewportWidth >= 990) {
        this.slidesPerView = this.reviewsDesktop;
        
        // iPad Pro specific optimizations
        if (deviceType && deviceType === 'ipad-pro') {
          this.touchThreshold = (this.touchThreshold || 30) * 0.9;
          this.swipeVelocity = (this.swipeVelocity || 0.5) * 1.2;
        }
      }
      // Tablet optimizations
      else if (viewportWidth >= 750) {
        this.slidesPerView = this.reviewsTablet;
        
        // Tablet-specific touch adjustments
        if (deviceType && deviceType.includes('ipad')) {
          this.touchThreshold = (this.touchThreshold || 30) * 0.85;
          this.swipeVelocity = (this.swipeVelocity || 0.5) * 1.3;
        }
      }
      // Large mobile optimizations
      else if (viewportWidth >= 481) {
        this.slidesPerView = Math.min(this.reviewsMobile + 1, (this.slides && this.slides.length) ? this.slides.length : 1);
        
        // iPhone Pro Max optimizations
        if (deviceType && deviceType === 'iphone-pro-max') {
          this.touchThreshold = (this.touchThreshold || 30) * 0.8;
          this.swipeVelocity = (this.swipeVelocity || 0.5) * 1.1;
        }
      }
      // Small mobile optimizations
      else {
        this.slidesPerView = this.reviewsMobile;
        
        // Small device specific optimizations
        if (deviceType && (deviceType.includes('legacy') || deviceType.includes('budget'))) {
          this.touchThreshold = (this.touchThreshold || 30) * 1.2;
          this.swipeVelocity = (this.swipeVelocity || 0.5) * 0.9;
          this.transitionDuration = Math.min(this.transitionDuration || 400, 300);
        }
      }
      
      // Apply battery optimizations if needed
      if (this.optimizations && this.optimizations.battery && this.optimizations.battery.enablePowerSaving) {
        this.transitionDuration = Math.min(this.transitionDuration || 400, 200);
        this.maxConcurrentAnimations = Math.min(this.maxConcurrentAnimations || 4, 2);
      }
    } catch (e) {
      // Error in applyViewportOptimizations - using fallback
      // Fallback to standard values
      this.slidesPerView = this.reviewsMobile || 1;
      this.touchThreshold = 30;
      this.swipeVelocity = 0.5;
      this.transitionDuration = 400;
      this.maxConcurrentAnimations = 4;
    }
  }
  
  calculateSlideWidths() {
    try {
      if (!this.slides || this.slides.length === 0) return;
      
      // CRITICAL FIX: Ensure wrapper exists and has valid dimensions
      if (!this.wrapper || !this.wrapper.offsetWidth) {
        // Wrapper not available for slide width calculation
        return;
      }
    
    const containerWidth = this.wrapper.offsetWidth;
      const baseGap = parseInt(getComputedStyle(this.wrapper).getPropertyValue('--review-gap')) || 20;
      
      if (this.slidesPerView <= 0) {
        this.slidesPerView = 1;
      }
      
      // CRITICAL FIX: Prevent division by zero
      if (this.slidesPerView <= 0) {
        this.slidesPerView = 1;
      }
      
      // Calculate base slide width
      let baseSlideWidth = (containerWidth - (baseGap * (this.slidesPerView - 1))) / this.slidesPerView;
      
      // CRITICAL FIX: Ensure valid slide width
      if (isNaN(baseSlideWidth) || baseSlideWidth <= 0) {
        // Invalid base slide width calculated, using fallback
        baseSlideWidth = containerWidth / this.slidesPerView;
      }
      
      // Apply clean device size adaptations
      this.slideWidth = this.applyCleanSizeMultipliers(baseSlideWidth);
      this.slideGap = this.applyCleanGapMultipliers(baseGap);
      this.slideWidthWithGap = this.slideWidth + this.slideGap;
      
      // CRITICAL FIX: Ensure final values are valid
      if (isNaN(this.slideWidth) || this.slideWidth <= 0) {
        // Invalid slide width calculated, using fallback
        this.slideWidth = baseSlideWidth;
        this.slideWidthWithGap = this.slideWidth + this.slideGap;
      }
      
      // Update slide widths with clean calculations
    this.updateSlideWidths();
      
      // Force layout recalculation for perfect alignment
      if (this.track) {
        this.track.offsetHeight;
      }
    } catch (e) {
      // Error in calculateSlideWidths - using fallback
      // Fallback to basic calculation
      if (this.wrapper && this.slides && this.slides.length > 0) {
        const containerWidth = this.wrapper.offsetWidth;
        this.slideWidth = containerWidth / this.slidesPerView;
        this.slideGap = 20;
        this.slideWidthWithGap = this.slideWidth + this.slideGap;
      }
    }
  }
  
  // CLEAN SIZE MULTIPLIER APPLICATION - NO BUGS
  applyCleanSizeMultipliers(baseWidth) {
    let finalWidth = baseWidth;
    
    // CRITICAL FIX: Only apply ONE device category multiplier based on current device
    const deviceCategory = (this.deviceCapabilities && this.deviceCapabilities.deviceType && this.deviceCapabilities.deviceType.category) ? this.deviceCapabilities.deviceType.category : 'mobile';
    
    if (deviceCategory === 'mobile' && this.mobileSlideMultiplier) {
      finalWidth *= this.mobileSlideMultiplier;
    } else if (deviceCategory === 'tablet' && this.tabletSlideMultiplier) {
      finalWidth *= this.tabletSlideMultiplier;
    } else if (deviceCategory === 'desktop' && this.desktopSlideMultiplier) {
      finalWidth *= this.desktopSlideMultiplier;
    }
    
    // Apply viewport multiplier (only one based on current viewport)
    const viewportWidth = window.innerWidth;
    if (viewportWidth >= 2560 && this.ultraWideMultiplier) {
      finalWidth *= this.ultraWideMultiplier;
    } else if (viewportWidth >= 1920 && this.largeScreenMultiplier) {
      finalWidth *= this.largeScreenMultiplier;
    } else if (viewportWidth <= 480 && this.smallMobileMultiplier) {
      finalWidth *= this.smallMobileMultiplier;
    }
    
    // Apply performance multiplier
    if (this.performanceSlideMultiplier) {
      finalWidth *= this.performanceSlideMultiplier;
    }
    
    // Ensure minimum and maximum constraints
    const minWidth = 200; // Minimum slide width
    const maxWidth = (this.wrapper && this.wrapper.offsetWidth) ? this.wrapper.offsetWidth * 0.8 : 600; // Maximum 80% of container or fallback
    
    return Math.max(minWidth, Math.min(finalWidth, maxWidth));
  }
  
  applyCleanGapMultipliers(baseGap) {
    let finalGap = baseGap;
    
    // CRITICAL FIX: Only apply ONE device category multiplier based on current device
    const deviceCategory = (this.deviceCapabilities && this.deviceCapabilities.deviceType && this.deviceCapabilities.deviceType.category) ? this.deviceCapabilities.deviceType.category : 'mobile';
    
    if (deviceCategory === 'mobile' && this.mobileGapMultiplier) {
      finalGap *= this.mobileGapMultiplier;
    } else if (deviceCategory === 'tablet' && this.tabletGapMultiplier) {
      finalGap *= this.tabletGapMultiplier;
    } else if (deviceCategory === 'desktop' && this.desktopGapMultiplier) {
      finalGap *= this.desktopGapMultiplier;
    }
    
    // Apply viewport multiplier (only one based on current viewport)
    const viewportWidth = window.innerWidth;
    if (viewportWidth >= 2560 && this.ultraWideGapMultiplier) {
      finalGap *= this.ultraWideGapMultiplier;
    } else if (viewportWidth >= 1920 && this.largeScreenGapMultiplier) {
      finalGap *= this.largeScreenGapMultiplier;
    } else if (viewportWidth <= 480 && this.smallMobileGapMultiplier) {
      finalGap *= this.smallMobileGapMultiplier;
    }
    
    // Apply performance multiplier
    if (this.performanceGapMultiplier) {
      finalGap *= this.performanceGapMultiplier;
    }
    
    // Ensure minimum and maximum constraints
    const minGap = 10; // Minimum gap
    const maxGap = 40; // Maximum gap
    
    return Math.max(minGap, Math.min(finalGap, maxGap));
  }
  
  // CRITICAL: LOCK CAROUSEL AGAINST SYSTEM BOLD TEXT SETTINGS - NO INTERFERENCE
  lockCarouselAgainstSystemSettings() {
    try {
      // Store original font weights and styles to prevent system changes
      this.storeOriginalCarouselStyles();
      
      // Apply system-immune styling
      this.applySystemImmuneStyles();
      
      // Monitor and prevent system setting changes
      this.monitorAndPreventSystemChanges();
      
      // Start monitoring for new content
      this.startContentMonitoring();
      
      // Force consistent appearance
      this.forceConsistentAppearance();
      
      // Carousel locked against system bold text settings
    } catch (e) {
      // Failed to lock carousel against system settings - using fallback
    }
  }
  
  // STORE ORIGINAL CAROUSEL STYLES - PRESERVE INTENDED APPEARANCE
  storeOriginalCarouselStyles() {
    if (!this.wrapper) return;
    
    this.originalStyles = {
      // Store original font weights
      fontWeights: {},
      // Store original line heights
      lineHeights: {},
      // Store original letter spacing
      letterSpacing: {},
      // Store original word spacing
      wordSpacing: {},
      // Store original text styles
      textStyles: {}
    };
    
    // Store styles for all carousel elements
    if (this.originalStyles && this.originalStyles.lineHeights) {
      this.storeElementStyles('.carousel-reviews__wrapper', 'wrapper');
      this.storeElementStyles('.carousel-reviews__track', 'track');
      this.storeElementStyles('.review-card', 'reviewCard');
      this.storeElementStyles('.review-card__text', 'reviewText');
      this.storeElementStyles('.review-card__customer-name', 'customerName');
      this.storeElementStyles('.review-card__title', 'reviewTitle');
      this.storeElementStyles('.carousel-reviews__nav--prev', 'prevButton');
      this.storeElementStyles('.carousel-reviews__nav--next', 'nextButton');
      this.storeElementStyles('.carousel-reviews__indicator', 'indicator');
    }
  }
  
  // STORE STYLES FOR SPECIFIC ELEMENT SELECTOR
  storeElementStyles(selector, key) {
    if (!this.wrapper || !this.originalStyles) return;
    
    const elements = this.wrapper.querySelectorAll(selector);
    if (elements.length === 0) return;
    
    const element = elements[0]; // Store from first element
    if (!element || !window || !window.getComputedStyle) return;
    const computedStyle = window.getComputedStyle(element);
    
    if (this.originalStyles && this.originalStyles.fontWeights) {
      this.originalStyles.fontWeights[key] = computedStyle.fontWeight;
    }
    if (this.originalStyles && this.originalStyles.lineHeights) {
      this.originalStyles.lineHeights[key] = computedStyle.lineHeight;
    }
    if (this.originalStyles && this.originalStyles.letterSpacing) {
      this.originalStyles.letterSpacing[key] = computedStyle.letterSpacing;
    }
    if (this.originalStyles && this.originalStyles.wordSpacing) {
      this.originalStyles.wordSpacing[key] = computedStyle.wordSpacing;
    }
    if (this.originalStyles && this.originalStyles.textStyles) {
      this.originalStyles.textStyles[key] = {
        fontFamily: computedStyle.fontFamily,
        fontSize: computedStyle.fontSize,
        fontStyle: computedStyle.fontStyle,
        textDecoration: computedStyle.textDecoration
      };
    }
  }
  
  // APPLY SYSTEM-IMMUNE STYLES - OVERRIDE ANY SYSTEM CHANGES
  applySystemImmuneStyles() {
    if (!this.wrapper || !this.originalStyles) return;
    
    // Apply to wrapper
    this.applyImmuneStylesToElement(this.wrapper, 'wrapper');
    
    // Apply to track
    if (this.track) {
      this.applyImmuneStylesToElement(this.track, 'track');
    }
    
    // Apply to all review cards
    const reviewCards = this.wrapper.querySelectorAll('.review-card');
    reviewCards.forEach(card => {
      this.applyImmuneStylesToElement(card, 'reviewCard');
      
      // Apply to card text elements
      const reviewText = card.querySelector('.review-card__text');
      if (reviewText) {
        this.applyImmuneStylesToElement(reviewText, 'reviewText');
      }
      
      const customerName = card.querySelector('.review-card__customer-name');
      if (customerName) {
        this.applyImmuneStylesToElement(customerName, 'customerName');
      }
      
      const reviewTitle = card.querySelector('.review-card__title');
      if (reviewTitle) {
        this.applyImmuneStylesToElement(reviewTitle, 'reviewTitle');
      }
    });
    
    // Apply to navigation buttons
    const prevButton = this.wrapper.querySelector('.carousel-reviews__nav--prev');
    if (prevButton) {
      this.applyImmuneStylesToElement(prevButton, 'prevButton');
    }
    
    const nextButton = this.wrapper.querySelector('.carousel-reviews__nav--next');
    if (nextButton) {
      this.applyImmuneStylesToElement(nextButton, 'nextButton');
    }
    
    // Apply to indicators
    const indicators = this.wrapper.querySelectorAll('.carousel-reviews__indicator');
    indicators.forEach(indicator => {
      this.applyImmuneStylesToElement(indicator, 'indicator');
    });
  }
  
  // APPLY IMMUNE STYLES TO SPECIFIC ELEMENT
  applyImmuneStylesToElement(element, key) {
    if (!element || !this.originalStyles || !this.originalStyles.fontWeights || !this.originalStyles.fontWeights[key] || !element.style) return;
    
    // Force original font weight - PREVENT SYSTEM BOLD TEXT
    element.style.setProperty('font-weight', this.originalStyles.fontWeights[key], 'important');
    
    // Force original line height - PREVENT SYSTEM LINE HEIGHT CHANGES
    if (this.originalStyles && this.originalStyles.lineHeights && this.originalStyles.lineHeights[key]) {
      element.style.setProperty('line-height', this.originalStyles.lineHeights[key], 'important');
    }
    
    // Force original letter spacing - PREVENT SYSTEM SPACING CHANGES
    if (this.originalStyles && this.originalStyles.letterSpacing && this.originalStyles.letterSpacing[key]) {
      element.style.setProperty('letter-spacing', this.originalStyles.letterSpacing[key], 'important');
    }
    
    // Force original word spacing - PREVENT SYSTEM SPACING CHANGES
    if (this.originalStyles && this.originalStyles.wordSpacing && this.originalStyles.wordSpacing[key]) {
      element.style.setProperty('word-spacing', this.originalStyles.wordSpacing[key], 'important');
    }
    
    // Force original text styles
    const textStyles = this.originalStyles.textStyles && this.originalStyles.textStyles[key];
    if (textStyles) {
      element.style.setProperty('font-family', textStyles.fontFamily, 'important');
      element.style.setProperty('font-size', textStyles.fontSize, 'important');
      element.style.setProperty('font-style', textStyles.fontStyle, 'important');
      element.style.setProperty('text-decoration', textStyles.textDecoration, 'important');
    }
    
    // Add immune class for CSS targeting
    element.classList.add('system-immune');
    
    // Set data attributes for debugging
    if (this.originalStyles && this.originalStyles.fontWeights && this.originalStyles.fontWeights[key]) {
      element.setAttribute('data-original-font-weight', this.originalStyles.fontWeights[key]);
    }
    if (this.originalStyles && this.originalStyles.lineHeights && this.originalStyles.lineHeights[key]) {
      element.setAttribute('data-original-line-height', this.originalStyles.lineHeights[key]);
    }
  }
  
  // MONITOR AND PREVENT SYSTEM SETTING CHANGES
  monitorAndPreventSystemChanges() {
    if (!this.originalStyles) return;
    
    // Monitor for system font weight changes
    this.monitorSystemFontChanges();
    
    // Monitor for accessibility setting changes
    this.monitorAccessibilityChanges();
    
    // Monitor for system preference changes
    this.monitorSystemPreferences();
    
    // Periodic check to ensure styles haven't been overridden
    this.startPeriodicStyleCheck();
  }
  
  // MONITOR SYSTEM FONT CHANGES
  monitorSystemFontChanges() {
    if (!this.originalStyles || !window.matchMedia) return;
    
    try {
      // Monitor for font weight preference changes
      const fontWeightQuery = window.matchMedia('(font-weight: bold)');
      if (fontWeightQuery && fontWeightQuery.addEventListener) {
        fontWeightQuery.addEventListener('change', () => {
          this.preventSystemFontChange();
        });
      }
      
      // Monitor for high contrast changes
      const highContrastQuery = window.matchMedia('(prefers-contrast: high)');
      if (highContrastQuery && highContrastQuery.addEventListener) {
        highContrastQuery.addEventListener('change', () => {
          this.preventSystemFontChange();
        });
      }
      
      // Monitor for reduced motion changes
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reducedMotionQuery && reducedMotionQuery.addEventListener) {
        reducedMotionQuery.addEventListener('change', () => {
          this.preventSystemFontChange();
        });
      }
    } catch (e) {
      // Error setting up system font monitoring - using fallback
    }
  }
  
  // MONITOR ACCESSIBILITY CHANGES
  monitorAccessibilityChanges() {
    if (!this.originalStyles) return;
    
    // Check for iOS/Android accessibility changes
    if (navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')) {
      this.monitorIOSAccessibility();
    } else if (navigator.userAgent.includes('Android')) {
      this.monitorAndroidAccessibility();
    }
  }
  
  // MONITOR iOS ACCESSIBILITY
  monitorIOSAccessibility() {
    if (!this.originalStyles) return;
    
    // iOS Bold Text setting can change dynamically
    setInterval(() => {
      this.checkIOSBoldTextChange();
    }, 2000); // Check every 2 seconds
  }
  
  // MONITOR ANDROID ACCESSIBILITY
  monitorAndroidAccessibility() {
    if (!this.originalStyles) return;
    
    // Android accessibility settings can change
    setInterval(() => {
      this.checkAndroidAccessibilityChange();
    }, 2000); // Check every 2 seconds
  }
  
  // MONITOR SYSTEM PREFERENCES
  monitorSystemPreferences() {
    // Monitor for any system preference changes that might affect text
    setInterval(() => {
      this.checkSystemPreferenceChanges();
    }, 3000); // Check every 3 seconds
  }
  
  // PREVENT SYSTEM FONT CHANGE
  preventSystemFontChange() {
    // System font change detected - preventing interference
    
    // Immediately re-apply immune styles
    this.applySystemImmuneStyles();
    
    // Force layout recalculation
    if (this.wrapper) {
      this.wrapper.offsetHeight;
    }
    if (this.track) {
      this.track.offsetHeight;
    }
  }
  
  // CHECK iOS BOLD TEXT CHANGE
  checkIOSBoldTextChange() {
    if (!this.originalStyles) return;
    
    try {
      // Check if iOS Bold Text has been enabled/disabled
      const testElement = document.createElement('div');
      testElement.style.fontWeight = 'bold';
      testElement.style.position = 'absolute';
      testElement.style.visibility = 'hidden';
      testElement.textContent = 'Test';
      document.body.appendChild(testElement);
      
      const computedStyle = window.getComputedStyle(testElement);
      const fontWeight = computedStyle.fontWeight;
      
      document.body.removeChild(testElement);
      
      // If system bold text changed, prevent interference
      if (parseInt(fontWeight) >= 700) {
        this.preventSystemFontChange();
      }
    } catch (e) {
      // Ignore errors, just prevent interference
      this.preventSystemFontChange();
    }
  }
  
  // CHECK ANDROID ACCESSIBILITY CHANGE
  checkAndroidAccessibilityChange() {
    if (!this.originalStyles) return;
    
    try {
      // Check for Android accessibility changes
      const testElement = document.createElement('div');
      testElement.style.fontWeight = 'bold';
      testElement.style.position = 'absolute';
      testElement.style.visibility = 'hidden';
      testElement.textContent = 'Test';
      document.body.appendChild(testElement);
      
      const computedStyle = window.getComputedStyle(testElement);
      const fontWeight = computedStyle.fontWeight;
      
      document.body.removeChild(testElement);
      
      // If system bold text changed, prevent interference
      if (parseInt(fontWeight) >= 600) {
        this.preventSystemFontChange();
      }
    } catch (e) {
      // Ignore errors, just prevent interference
      this.preventSystemFontChange();
    }
  }
  
  // CHECK SYSTEM PREFERENCE CHANGES
  checkSystemPreferenceChanges() {
    if (!this.originalStyles) return;
    
    try {
      // Check for any system preference changes
      const prefersHighContrast = matchMedia('(prefers-contrast: high)').matches;
      const prefersReducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      // If any system preference changed, prevent interference
      if (prefersHighContrast || prefersReducedMotion) {
        this.preventSystemFontChange();
      }
    } catch (e) {
      // Ignore errors, just prevent interference
      this.preventSystemFontChange();
    }
  }
  
  // START PERIODIC STYLE CHECK
  startPeriodicStyleCheck() {
    if (!this.originalStyles) return;
    
    // Check every 1 second to ensure styles haven't been overridden
    setInterval(() => {
      this.verifyStylesAreImmune();
    }, 1000);
  }
  
  // VERIFY STYLES ARE STILL IMMUNE
  verifyStylesAreImmune() {
    try {
      if (!this.wrapper || !this.originalStyles) return;
      
      // Check if any system changes have overridden our immune styles
      const elements = this.wrapper.querySelectorAll('.system-immune');
      let needsReapplication = false;
      
      elements.forEach(element => {
        const currentFontWeight = window.getComputedStyle(element).fontWeight;
        const originalFontWeight = element.getAttribute('data-original-font-weight');
        
        // If font weight changed from system, reapply immune styles
        if (currentFontWeight !== originalFontWeight) {
          needsReapplication = true;
        }
      });
      
      // If any styles were overridden, reapply immune styles
      if (needsReapplication) {
        // System override detected - reapplying immune styles
        this.applySystemImmuneStyles();
      }
    } catch (e) {
      // Ignore errors, just reapply immune styles
      if (this.originalStyles) {
        this.applySystemImmuneStyles();
      }
    }
  }
  
  // FORCE CONSISTENT APPEARANCE
  forceConsistentAppearance() {
    if (!this.wrapper || !this.originalStyles) return;
    
    // Add CSS variables to prevent system interference
    if (this.wrapper && this.wrapper.style) {
      this.wrapper.style.setProperty('--system-immune-font-weight', 'normal', 'important');
      this.wrapper.style.setProperty('--system-immune-line-height', 'var(--review-text-line-height, var(--line-height, 1.6))', 'important');
      this.wrapper.style.setProperty('--system-immune-letter-spacing', '0px', 'important');
      this.wrapper.style.setProperty('--system-immune-word-spacing', '0px', 'important');
    }
    
    // Add system-immune class to wrapper
    if (this.wrapper && this.wrapper.classList) {
      this.wrapper.classList.add('system-immune-carousel');
    }
    
    // Force immediate style application
    if (this.wrapper.offsetHeight !== undefined) {
      this.wrapper.offsetHeight;
      this.wrapper.offsetHeight;
    }
  }
  
  // MAINTAIN SYSTEM IMMUNITY AFTER ANY UPDATES
  maintainSystemImmunity() {
    // If system immunity is already active, reapply to any new elements
    if (this.originalStyles && this.originalStyles.fontWeights) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (this.originalStyles) {
          this.applySystemImmuneStyles();
        }
      }, 50);
    }
  }
  
  // REFRESH SYSTEM IMMUNITY - CALL THIS WHEN CAROUSEL IS UPDATED
  refreshSystemImmunity() {
    try {
      // Refreshing system immunity for carousel updates
      
      // Re-store original styles if needed
      if (!this.originalStyles || !this.originalStyles.fontWeights) {
        this.storeOriginalCarouselStyles();
      }
      
      // Re-apply system immunity
      if (this.originalStyles) {
        this.applySystemImmuneStyles();
      }
      
      // Force layout recalculation
      if (this.wrapper) {
        this.wrapper.offsetHeight;
      }
      if (this.track) {
        this.track.offsetHeight;
      }
      
      // System immunity refreshed successfully
    } catch (e) {
      // Failed to refresh system immunity - using fallback
    }
  }
  
  // CLEANUP SYSTEM IMMUNITY - CALL THIS WHEN CAROUSEL IS DESTROYED
  cleanupSystemImmunity() {
    try {
      // Cleaning up system immunity
      
      if (!this.wrapper) return;
      
      // Remove all system-immune classes
      if (this.wrapper.querySelectorAll) {
        const immuneElements = this.wrapper.querySelectorAll('.system-immune');
        immuneElements.forEach(element => {
          if (element.classList) {
            element.classList.remove('system-immune');
          }
          element.removeAttribute('data-original-font-weight');
          element.removeAttribute('data-original-line-height');
        });
      }
      
      // Remove wrapper immune class
      if (this.wrapper.classList) {
        this.wrapper.classList.remove('system-immune-carousel');
      }
      
      // Clear original styles
      this.originalStyles = null;
      
      // Clear all intervals
      if (this.systemImmunityIntervals) {
        this.systemImmunityIntervals.forEach(interval => clearInterval(interval));
        this.systemImmunityIntervals = [];
      }
      
      // CRITICAL: Cleanup layout monitoring
      this.cleanupLayoutMonitoring();
      
      // System immunity cleaned up successfully
    } catch (e) {
      // Failed to cleanup system immunity - using fallback
    }
  }
  
  // HANDLE NEW CONTENT ADDED TO CAROUSEL
  handleNewContentAdded() {
    try {
      if (!this.wrapper) return;
      
      // New content detected - applying system immunity
      
      // Find new elements that don't have system immunity
      const newElements = this.wrapper.querySelectorAll(':not(.system-immune)');
      
      newElements.forEach(element => {
        // Check if this is a carousel element that needs immunity
        if (this.isCarouselElement(element)) {
          this.applySystemImmunityToNewElement(element);
        }
      });
      
      // Refresh system immunity for all elements
      if (this.originalStyles) {
        this.refreshSystemImmunity();
      }
      
      // System immunity applied to new content
    } catch (e) {
      // Failed to handle new content - using fallback
    }
  }
  
  // CHECK IF ELEMENT IS A CAROUSEL ELEMENT THAT NEEDS IMMUNITY
  isCarouselElement(element) {
    if (!element) return false;
    
    // Check if element is part of the carousel structure
    return element.classList.contains('carousel-reviews__wrapper') ||
           element.classList.contains('carousel-reviews__track') ||
           element.classList.contains('review-card') ||
           element.classList.contains('review-card__text') ||
           element.classList.contains('review-card__customer-name') ||
           element.classList.contains('review-card__title') ||
           element.classList.contains('carousel-reviews__nav--prev') ||
           element.classList.contains('carousel-reviews__nav--next') ||
           element.classList.contains('carousel-reviews__indicator') ||
           element.classList.contains('continuous-clone');
  }
  
  // APPLY SYSTEM IMMUNITY TO NEW ELEMENT
  applySystemImmunityToNewElement(element) {
    if (!element || !this.originalStyles) return;
    
    // Determine which style key to use based on element type
    let styleKey = 'reviewCard'; // Default
    
    if (element.classList.contains('review-card__text')) {
      styleKey = 'reviewText';
    } else if (element.classList.contains('review-card__customer-name')) {
      styleKey = 'customerName';
    } else if (element.classList.contains('review-card__title')) {
      styleKey = 'reviewTitle';
    } else if (element.classList.contains('carousel-reviews__nav--prev')) {
      styleKey = 'prevButton';
    } else if (element.classList.contains('carousel-reviews__nav--next')) {
      styleKey = 'nextButton';
    } else if (element.classList.contains('carousel-reviews__indicator')) {
      styleKey = 'indicator';
    } else if (element.classList.contains('carousel-reviews__wrapper')) {
      styleKey = 'wrapper';
    } else if (element.classList.contains('carousel-reviews__track')) {
      styleKey = 'track';
    }
    
    // Apply system immunity
    this.applyImmuneStylesToElement(element, styleKey);
  }
  
  // MONITOR FOR NEW CONTENT ADDED TO CAROUSEL
  startContentMonitoring() {
    // Use MutationObserver to detect when new content is added
    if (window.MutationObserver) {
      this.contentObserver = new MutationObserver((mutations) => {
        let hasNewContent = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                if (this.isCarouselElement(node)) {
                  hasNewContent = true;
                }
              }
            });
          }
        });
        
        if (hasNewContent) {
          // Small delay to ensure DOM is fully updated
          setTimeout(() => {
            if (this.originalStyles) {
              this.handleNewContentAdded();
            }
          }, 100);
        }
      });
      
      // Start observing the carousel wrapper for changes
      if (this.wrapper && this.contentObserver) {
        this.contentObserver.observe(this.wrapper, {
          childList: true,
          subtree: true
        });
      }
      
      // Content monitoring started for system immunity
    }
  }
  
  // CLEAN CSS CLASSES FOR STYLING - NO BUGS
  applyCleanCSSClasses(viewportWidth) {
    if (!this.wrapper) return;
    
    const deviceType = (this.deviceCapabilities && this.deviceCapabilities.deviceType && this.deviceCapabilities.deviceType.type) ? this.deviceCapabilities.deviceType.type : 'unknown';
    const deviceCategory = (this.deviceCapabilities && this.deviceCapabilities.deviceType && this.deviceCapabilities.deviceType.category) ? this.deviceCapabilities.deviceType.category : 'mobile';
    const performanceTier = (this.deviceCapabilities && this.deviceCapabilities.performanceTier) ? this.deviceCapabilities.performanceTier : 'standard';
    
    // Remove all existing device classes
    this.wrapper.classList.remove(
      'device-mobile', 'device-tablet', 'device-desktop',
      'device-iphone-pro', 'device-ipad-pro', 'device-android-premium',
      'device-android-budget', 'device-high-end-desktop'
    );
    
    // Add device category class
    this.wrapper.classList.add(`device-${deviceCategory}`);
    
    // Add specific device type class
    if (deviceType !== 'unknown') {
      this.wrapper.classList.add(`device-${deviceType}`);
    }
    
    // Remove all existing performance classes
    this.wrapper.classList.remove(
      'performance-ultra-premium', 'performance-premium', 'performance-high',
      'performance-standard', 'performance-basic', 'performance-minimal'
    );
    
    // Add performance tier class
    this.wrapper.classList.add(`performance-${performanceTier}`);
    
    // Remove all existing breakpoint classes
    this.wrapper.classList.remove(
      'breakpoint-ultra-wide', 'breakpoint-large-desktop', 'breakpoint-desktop',
      'breakpoint-tablet', 'breakpoint-large-mobile', 'breakpoint-mobile'
    );
    
    // Add breakpoint class
    if (viewportWidth >= 2560) {
      this.wrapper.classList.add('breakpoint-ultra-wide');
    } else if (viewportWidth >= 1920) {
      this.wrapper.classList.add('breakpoint-large-desktop');
    } else if (viewportWidth >= 1200) {
      this.wrapper.classList.add('breakpoint-desktop');
    } else if (viewportWidth >= 750) {
      this.wrapper.classList.add('breakpoint-tablet');
    } else if (viewportWidth >= 481) {
      this.wrapper.classList.add('breakpoint-large-mobile');
    } else {
      this.wrapper.classList.add('breakpoint-mobile');
    }
    
    // Add slides per view class
    this.wrapper.classList.remove('slides-1', 'slides-2', 'slides-3', 'slides-4', 'slides-5');
    this.wrapper.classList.add(`slides-${this.slidesPerView}`);
  }
  
  updateSlideWidths() {
    try {
      // CRITICAL FIX: Ensure track exists
      if (!this.track) {
        // Track not available for slide width update
        return;
      }
      
    const allSlides = this.track.querySelectorAll('.carousel-reviews__slide');
      
      // CRITICAL FIX: Ensure slide width is valid
      if (!this.slideWidth || isNaN(this.slideWidth) || this.slideWidth <= 0) {
        // Invalid slide width, using fallback
        this.slideWidth = 300; // Fallback width
      }
      
      // CRITICAL FIX: Use clean calculated gap instead of CSS variable
      let gap = this.slideGap || (this.wrapper ? parseInt(getComputedStyle(this.wrapper).getPropertyValue('--review-gap')) || 20 : 20);
      
      // CRITICAL FIX: Ensure gap is valid
      if (isNaN(gap) || gap < 0) {
        // Invalid gap value, using fallback
        gap = 20;
      }
      
      // Apply clean slide widths and gaps
    allSlides.forEach((slide, index) => {
        try {
      slide.style.width = `${this.slideWidth}px`;
          // Apply calculated gap to all slides except the very last one
      slide.style.marginRight = index === allSlides.length - 1 ? '0' : `${gap}px`;
        } catch (slideError) {
          // Error updating slide - using fallback
        }
      });
      
      // Force layout recalculation for perfect alignment
      if (this.track) {
        this.track.offsetHeight;
      }
    } catch (e) {
      // Error in updateSlideWidths - using fallback
      // Fallback to basic update
      if (this.track) {
        const allSlides = this.track.querySelectorAll('.carousel-reviews__slide');
        allSlides.forEach((slide, index) => {
          slide.style.width = '300px';
          slide.style.marginRight = index === allSlides.length - 1 ? '0' : '20px';
        });
      }
    }
  }
  
  setupCarousel() {
    if (!this.track) return;
    
    this.track.style.transitionDuration = `${this.transitionDuration}ms`;
    this.track.setAttribute('data-scroll-behavior', this.scrollBehavior);
    
    if (this.scrollBehavior === 'smooth' && this.currentIndex > 0) {
      this.setTransform(this.getTranslateValue(), false);
    }
  }
  
  setupContinuousMode() {
    if (!this.track || !this.track.children) return;
    
    // FIXED: Create seamless loop by duplicating all slides multiple times
    const originalSlides = Array.from ? Array.from(this.track.children) : [];
    
    // CRITICAL FIX: Create only necessary clones for seamless looping
    for (let i = 0; i < 2; i++) {
      originalSlides.forEach(slide => {
        if (slide && this.track) {
          const clone = slide.cloneNode(true);
          clone.classList.add('continuous-clone');
          this.track.appendChild(clone);
        }
      });
    }
    
    // Apply clean gap styling to all slides including clones
    this.updateSlideWidths();
    
    // Recalculate slide widths after cloning to ensure perfect alignment
    this.calculateSlideWidths();
    
    // CRITICAL: Apply system immunity to cloned slides
    this.applySystemImmunityToClones();
    
    // CRITICAL FIX: Remove transition for continuous scrolling
    this.track.style.transition = 'none';
    // CRITICAL FIX: Initialize continuous position with validation
    if (typeof this.continuousPosition !== 'number' || isNaN(this.continuousPosition)) {
      this.continuousPosition = 0;
    }
    
    // Enable horizontal scrolling
    this.track.style.overflowX = 'auto';
    this.track.style.scrollBehavior = 'smooth';
    
    // FIXED: ULTIMATE immediate gap alignment - force layout calculation multiple times
    this.track.style.transform = `translateX(${this.continuousPosition}px)`;
    this.track.offsetHeight; // Force immediate reflow
    
    // CRITICAL FIX: Single requestAnimationFrame for smooth initialization
    requestAnimationFrame(() => {
      if (this.track) {
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${this.continuousPosition}px)`;
        this.track.offsetHeight;
      }
    });
  }
  
  // APPLY SYSTEM IMMUNITY TO CLONED SLIDES
  applySystemImmunityToClones() {
    if (!this.originalStyles || !this.originalStyles.fontWeights || !this.track) return;
    
    // Find all cloned slides
    const clonedSlides = this.track.querySelectorAll('.continuous-clone');
    
    clonedSlides.forEach(clone => {
      // Apply system immunity to the clone itself
      this.applyImmuneStylesToElement(clone, 'reviewCard');
      
      // Apply system immunity to all text elements within the clone
      const reviewText = clone.querySelector('.review-card__text');
      if (reviewText) {
        this.applyImmuneStylesToElement(reviewText, 'reviewText');
      }
      
      const customerName = clone.querySelector('.review-card__customer-name');
      if (customerName) {
        this.applyImmuneStylesToElement(customerName, 'customerName');
      }
      
      const reviewTitle = clone.querySelector('.review-card__title');
      if (reviewTitle) {
        this.applyImmuneStylesToElement(reviewTitle, 'reviewTitle');
      }
    });
  }
  
  cloneSlides() {
    if (!this.slides || this.slides.length === 0 || !this.track) return;
    
    const clonesNeeded = Math.max(this.slidesPerView, 2);
    
    for (let i = 0; i < clonesNeeded; i++) {
      if (this.slides && this.slides.length > 0) {
      const clone = this.slides[i % this.slides.length].cloneNode(true);
      clone.classList.add('carousel-clone');
      this.track.appendChild(clone);
      }
    }
    
    for (let i = 0; i < clonesNeeded; i++) {
      if (this.slides && this.slides.length > 0) {
      const index = this.slides.length - 1 - i;
      const clone = this.slides[index].cloneNode(true);
      clone.classList.add('carousel-clone');
      this.track.insertBefore(clone, this.track.firstChild);
      }
    }
    
    this.currentIndex = clonesNeeded;
    
    requestAnimationFrame(() => {
      this.calculateSlideWidths();
      this.setTransform(this.getTranslateValue(), false);
    });
  }
  
  bindEvents() {
    if (!this.wrapper) return;
    
    // Navigation buttons (only for non-continuous modes)
    if (this.scrollBehavior !== 'continuous') {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }
    
    if (this.indicators && this.indicators.length > 0) {
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.goToSlide(index));
    });
    }
    }
    
    // ISOLATED: Desktop hover events (separate from touch events)
    if (this.pauseOnHover && !this.isMobile && this.wrapper) {
      this.wrapper.addEventListener('mouseenter', () => {
        this.hoverState.isHovered = true;
        this.pauseAutoplay('hover');
      });
      this.wrapper.addEventListener('mouseleave', () => {
        this.hoverState.isHovered = false;
        this.resumeAutoplay('hover');
      });
    }
    
    // ISOLATED: Mobile touch events for partial touch/hold (separate from navigation)
    if (this.isMobile && this.pauseOnHover && this.wrapper) {
      this.wrapper.addEventListener('touchstart', (e) => this.handleMobileTouchStart(e), { passive: true });
      this.wrapper.addEventListener('touchmove', (e) => this.handleMobileTouchMove(e), { passive: true });
      this.wrapper.addEventListener('touchend', (e) => this.handleMobileTouchEnd(e), { passive: true });
      this.wrapper.addEventListener('touchcancel', (e) => this.handleMobileTouchEnd(e), { passive: true });
    }
    
    // Clean resize handler - NO BUGS
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // Store old values for comparison
        const oldSlidesPerView = this.slidesPerView;
        const oldSlideWidth = this.slideWidth;
        
        // Update responsive settings with clean device adaptations
        this.updateResponsiveSettings();
        
        // Handle slides per view changes
        if (oldSlidesPerView !== this.slidesPerView) {
          this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
          
          // Smooth transition for layout changes
          if (this.scrollBehavior !== 'continuous') {
            this.setTransform(this.getTranslateValue(), true);
          }
        }
        
        // Handle slide width changes for continuous mode
        if (this.scrollBehavior === 'continuous' && oldSlideWidth !== this.slideWidth) {
          // CRITICAL FIX: Recalculate continuous position to maintain visual consistency
          if (this.continuousPosition !== undefined && this.track && this.track.style && oldSlideWidth > 0) {
            const ratio = this.slideWidth / oldSlideWidth;
            this.continuousPosition *= ratio;
            // Ensure position stays within bounds
            const maxPosition = (this.slides && this.slides.length > 0) ? this.slides.length * this.slideWidthWithGap : 0;
            if (this.continuousPosition < -maxPosition) {
              this.continuousPosition = -maxPosition;
            }
            this.track.style.transform = `translateX(${this.continuousPosition}px)`;
          }
        }
        
        // Update display and line heights
        this.updateDisplay();
        this.applyLineHeightStyles(true); // Force apply after resize
        this.applyTextSpacingStyles(true); // Force apply text spacing after resize
        
        // CRITICAL FIX: Force apply line height setting after resize
        setTimeout(() => {
          this.forceApplyLineHeightSetting();
        }, 100);
        
        // Force layout recalculation
        if (this.wrapper && this.wrapper.offsetHeight !== undefined) {
          this.wrapper.offsetHeight;
        }
        if (this.track && this.track.offsetHeight !== undefined) {
          this.track.offsetHeight;
        }
      }, 100); // Faster response for better user experience
    });
    
    // Keyboard navigation (only for non-continuous modes)
    if (this.scrollBehavior !== 'continuous' && this.wrapper && this.wrapper.addEventListener) {
    this.wrapper.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.nextSlide();
      }
    });
    }
    
    // ISOLATED: Visibility change (separate from user interactions)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoplay('visibility');
      } else if (this.autoplay) {
        this.resumeAutoplay('visibility');
      }
    });
  }
  
  // NEW: Mobile touch handlers for partial touch/hold functionality
  handleMobileTouchStart(e) {
    if (this.scrollBehavior !== 'continuous') return;
    
    // CRITICAL FIX: Ensure mobileTouchState exists before accessing properties
    if (!this.mobileTouchState) {
      this.mobileTouchState = {
        isTouching: false,
        isPartialTouchHold: false,
        touchStartTime: 0,
        touchHoldTimer: null,
        lastTouchPosition: null,
        touchMoveDistance: 0,
        isHorizontalScroll: false,
        isVerticalScrolling: false,
        scrollPauseTimer: null,
        shouldResumeFromUserPosition: false
      };
    }
    
    if (this.mobileTouchState) {
      this.mobileTouchState.isTouching = true;
      this.mobileTouchState.touchStartTime = Date.now();
      this.mobileTouchState.lastTouchPosition = {
        x: (e.touches && e.touches[0]) ? e.touches[0].clientX : 0,
        y: (e.touches && e.touches[0]) ? e.touches[0].clientY : 0
      };
      this.mobileTouchState.touchMoveDistance = 0;
      this.mobileTouchState.isHorizontalScroll = false;
      this.mobileTouchState.isVerticalScrolling = false;
    }
    
    // FIXED: Clear any existing hold timer
    if (this.mobileTouchState && this.mobileTouchState.touchHoldTimer) {
      clearTimeout(this.mobileTouchState.touchHoldTimer);
      this.mobileTouchState.touchHoldTimer = null;
    }
    
    // Start timer for partial touch hold detection (300ms)
    if (this.mobileTouchState) {
      this.mobileTouchState.touchHoldTimer = setTimeout(() => {
        if (this.mobileTouchState && this.mobileTouchState.isTouching && this.mobileTouchState.touchMoveDistance < 30) {
          this.mobileTouchState.isPartialTouchHold = true;
          this.pauseAutoplay('touch');
        }
      }, 300); // CRITICAL FIX: Reduced from 500ms to 300ms for faster detection
    }
  }
  
  handleMobileTouchMove(e) {
    if (this.scrollBehavior !== 'continuous') return;
    
    // CRITICAL FIX: Ensure mobileTouchState exists before accessing properties
    if (!this.mobileTouchState) {
      this.mobileTouchState = {
        isTouching: false,
        isPartialTouchHold: false,
        touchStartTime: 0,
        touchHoldTimer: null,
        lastTouchPosition: null,
        touchMoveDistance: 0,
        isHorizontalScroll: false,
        isVerticalScrolling: false,
        scrollPauseTimer: null,
        shouldResumeFromUserPosition: false
      };
    }
    
    if (!this.mobileTouchState.isTouching) return;
    
    const currentTouch = {
      x: (e.touches && e.touches[0]) ? e.touches[0].clientX : 0,
      y: (e.touches && e.touches[0]) ? e.touches[0].clientY : 0
    };
    
    const deltaX = (this.mobileTouchState.lastTouchPosition) ? Math.abs(currentTouch.x - this.mobileTouchState.lastTouchPosition.x) : 0;
    const deltaY = (this.mobileTouchState.lastTouchPosition) ? Math.abs(currentTouch.y - this.mobileTouchState.lastTouchPosition.y) : 0;
    
    if (this.mobileTouchState) {
      this.mobileTouchState.touchMoveDistance = Math.max(this.mobileTouchState.touchMoveDistance || 0, Math.max(deltaX, deltaY));
      this.mobileTouchState.lastTouchPosition = currentTouch;
    }
    
    // Detect scroll direction
    if (deltaX > deltaY && deltaX > 10) {
      if (this.mobileTouchState) {
        this.mobileTouchState.isHorizontalScroll = true;
        // Store user's scroll position for horizontal scrolling
        this.mobileTouchState.userScrollPosition = (this.track && this.track.scrollLeft !== undefined) ? this.track.scrollLeft : 0;
        this.mobileTouchState.shouldResumeFromUserPosition = true;
      }
      this.pauseAutoplay('touch');
    } else if (deltaY > deltaX && deltaY > 10) {
      if (this.mobileTouchState) {
        this.mobileTouchState.isVerticalScrolling = true;
      }
    }
    
    // FIXED: Clear hold timer if user moves significantly - ensure consistent behavior
    if (this.mobileTouchState && this.mobileTouchState.touchMoveDistance > 30 && this.mobileTouchState.touchHoldTimer) {
      clearTimeout(this.mobileTouchState.touchHoldTimer);
      this.mobileTouchState.touchHoldTimer = null;
    }
  }
  
  handleMobileTouchEnd(e) {
    if (this.scrollBehavior !== 'continuous') return;
    
    // CRITICAL FIX: Ensure mobileTouchState exists before accessing properties
    if (!this.mobileTouchState) {
      this.mobileTouchState = {
        isTouching: false,
        isPartialTouchHold: false,
        touchStartTime: 0,
        touchHoldTimer: null,
        lastTouchPosition: null,
        touchMoveDistance: 0,
        isHorizontalScroll: false,
        isVerticalScrolling: false,
        scrollPauseTimer: null,
        shouldResumeFromUserPosition: false
      };
    }
    
    const touchDuration = (this.mobileTouchState && this.mobileTouchState.touchStartTime) ? Date.now() - this.mobileTouchState.touchStartTime : 0;
    
    // FIXED: Clear hold timer immediately
    if (this.mobileTouchState && this.mobileTouchState.touchHoldTimer) {
      clearTimeout(this.mobileTouchState.touchHoldTimer);
      this.mobileTouchState.touchHoldTimer = null;
    }
    
    // FIXED: Clear any existing scroll pause timer
    if (this.mobileTouchState && this.mobileTouchState.scrollPauseTimer) {
      clearTimeout(this.mobileTouchState.scrollPauseTimer);
      this.mobileTouchState.scrollPauseTimer = null;
    }
    
    // FIXED: Reset all touch states immediately
    if (this.mobileTouchState) {
      this.mobileTouchState.isTouching = false;
      this.mobileTouchState.isHorizontalScroll = false;
      this.mobileTouchState.isVerticalScrolling = false;
    }
    
    // CRITICAL FIX: Immediate resume check for faster autoplay recovery
    if (this.autoplayState && this.autoplayState.isPaused && this.autoplayState.pauseReason === 'touch') {
      // If paused by touch and no specific touch scenario, resume immediately
      if (!this.mobileTouchState.isPartialTouchHold && !this.mobileTouchState.isHorizontalScroll && !this.mobileTouchState.isVerticalScrolling) {
        this.autoplayState.pauseReason = null;
        this.autoplayState.isPaused = false;
        this.resumeAutoplay('touch');
        return; // Exit early to prevent other resume logic
      }
    }
    
    // Handle different touch scenarios
    if (this.mobileTouchState && this.mobileTouchState.isVerticalScrolling) {
      // Vertical scroll - don't interfere with autoplay
      this.mobileTouchState.isVerticalScrolling = false;
    } else if (this.mobileTouchState && this.mobileTouchState.isHorizontalScroll) {
      // Horizontal scroll - pause briefly then resume from user position
      this.mobileTouchState.isHorizontalScroll = false;
      if (this.mobileTouchState && this.mobileTouchState.scrollPauseTimer) {
        clearTimeout(this.mobileTouchState.scrollPauseTimer);
        this.mobileTouchState.scrollPauseTimer = null;
      }
      if (this.mobileTouchState) {
        this.mobileTouchState.scrollPauseTimer = setTimeout(() => {
          this.resumeAutoplayFromUserPosition();
        }, 300); // CRITICAL FIX: Reduced from 2000ms to 300ms for faster resume
      }
    } else if (this.mobileTouchState && this.mobileTouchState.isPartialTouchHold) {
      // Partial hold - resume after brief delay
      this.mobileTouchState.isPartialTouchHold = false;
      if (this.mobileTouchState && this.mobileTouchState.scrollPauseTimer) {
        clearTimeout(this.mobileTouchState.scrollPauseTimer);
        this.mobileTouchState.scrollPauseTimer = null;
      }
      if (this.mobileTouchState) {
        this.mobileTouchState.scrollPauseTimer = setTimeout(() => {
          // CRITICAL FIX: Ensure autoplay state is properly reset before resuming
          if (this.autoplayState) {
            this.autoplayState.pauseReason = null;
            this.autoplayState.isPaused = false;
          }
          this.resumeAutoplay('touch');
        }, 200); // CRITICAL FIX: Reduced from 1000ms to 200ms for faster resume
      }
    } else if (touchDuration < 200 && this.mobileTouchState && this.mobileTouchState.touchMoveDistance !== undefined && this.mobileTouchState.touchMoveDistance < 10) {
      // Quick tap - resume immediately for faster response
      if (this.autoplayState && this.autoplayState.isPaused) {
        this.autoplayState.pauseReason = null;
        this.autoplayState.isPaused = false;
        this.resumeAutoplay('touch');
      }
    }
  }
  
  initTouchEvents() {
    if (!this.track) return;
    
    // Only enable swipe for non-continuous modes
    if (this.scrollBehavior === 'continuous') {
      this.initContinuousTouchEvents();
      return;
    }
    
    if (this.track.addEventListener) {
    this.track.addEventListener('touchstart', (e) => this.touchStart(e), { passive: true });
    this.track.addEventListener('touchmove', (e) => this.touchMove(e), { passive: false });
      this.track.addEventListener('touchend', (e) => this.touchEnd(e));
      this.track.addEventListener('touchcancel', (e) => this.touchEnd(e));
    
    this.track.addEventListener('mousedown', (e) => this.touchStart(e));
    this.track.addEventListener('mousemove', (e) => this.touchMove(e));
      this.track.addEventListener('mouseup', (e) => this.touchEnd(e));
      this.track.addEventListener('mouseleave', (e) => this.touchEnd(e));
    }
    
    if (this.track.addEventListener) {
    this.track.addEventListener('contextmenu', (e) => {
        if (this.touchNavigationState && this.touchNavigationState.isDragging) e.preventDefault();
      });
      
      this.track.addEventListener('selectstart', (e) => {
        if (this.touchNavigationState && this.touchNavigationState.isDragging) e.preventDefault();
      });
    }
    
    window.addEventListener('blur', () => {
      if (this.touchNavigationState && this.touchNavigationState.isDragging) {
        this.resetTouchState();
      }
    });
    
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.touchNavigationState && this.touchNavigationState.isDragging) {
        this.resetTouchState();
      }
    });
  }
  
  touchStart(e) {
    if (this.scrollBehavior === 'continuous' || !this.touchNavigationState) return;
    if (this.touchNavigationState && this.touchNavigationState.isDragging) return;
    
    if (this.touchNavigationState) {
      this.touchNavigationState.startX = this.getPositionX(e);
      this.touchNavigationState.startY = this.getPositionY(e);
      this.touchNavigationState.initialTranslate = this.getTranslateValue();
      this.touchNavigationState.hasMoved = false;
      this.touchNavigationState.touchStartTime = Date.now();
    }
    
    if (this.touchNavigationState) {
      this.touchNavigationState.touchTimeout = setTimeout(() => {
        if (this.touchNavigationState && this.touchNavigationState.isDragging) {
          this.resetTouchState();
        }
      }, 2000); // CRITICAL FIX: Reduced from 5000ms to 2000ms for faster timeout
    }
  }
  
  touchMove(e) {
    if (this.scrollBehavior === 'continuous' || !this.touchNavigationState) return;
    
    let deltaX = 0;
    let deltaY = 0;
    
    if (this.touchNavigationState) {
      this.touchNavigationState.currentX = this.getPositionX(e);
      this.touchNavigationState.currentY = this.getPositionY(e);
      deltaX = this.touchNavigationState.currentX - this.touchNavigationState.startX;
      deltaY = this.touchNavigationState.currentY - this.touchNavigationState.startY;
    }
    
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 15) {
      if (this.touchNavigationState) {
        this.touchNavigationState.isVerticalScrolling = true;
      }
      return;
    }
    
    // FIXED: Lower thresholds for better mobile swipe detection
    if (this.touchNavigationState && !this.touchNavigationState.isDragging && 
        !this.touchNavigationState.isVerticalScrolling &&
        Math.abs(deltaX) > Math.abs(deltaY) && 
        Math.abs(deltaX) > 15 && // Lowered from 25 to 15 for easier activation
        Math.abs(deltaY) < 25) { // Increased from 20 to 25 for better tolerance
              this.touchNavigationState.isDragging = true;
        
        if (this.wrapper) {
    this.wrapper.dataset.dragging = 'true';
        }
    
        this.pauseAutoplay('manual');
        
        if (this.track) {
    this.track.style.cursor = 'grabbing';
  }
  
        e.preventDefault();
    }
    
    if (this.touchNavigationState && this.touchNavigationState.isDragging) {
    e.preventDefault();
      if (Math.abs(deltaX) > 5) {
        this.touchNavigationState.hasMoved = true;
      }
      
      this.touchNavigationState.currentTranslate = this.touchNavigationState.initialTranslate + deltaX;
      // NUCLEAR FIX: Direct execution for maximum touch smoothness
      this.setTransform(this.touchNavigationState.currentTranslate, false);
    }
  }
  
  touchEnd() {
    if (this.scrollBehavior === 'continuous' || !this.touchNavigationState) return;
    
    if (this.touchNavigationState && this.touchNavigationState.touchTimeout) {
      clearTimeout(this.touchNavigationState.touchTimeout);
      this.touchNavigationState.touchTimeout = null;
    }
    
    if (this.touchNavigationState && this.touchNavigationState.isVerticalScrolling) {
      this.resetTouchState();
      return;
    }
    
    if (this.touchNavigationState && this.touchNavigationState.isDragging) {
      const deltaX = this.touchNavigationState.currentX - this.touchNavigationState.startX;
      // FIXED: Lower threshold for easier swipe detection on mobile
      const threshold = this.touchThreshold || 30; // Lowered from 50 to 30
      const touchDuration = Date.now() - this.touchNavigationState.touchStartTime;
      
      // FIXED: Handle manual navigation swipe properly
      if (this.touchNavigationState && this.touchNavigationState.hasMoved && Math.abs(deltaX) > threshold && touchDuration > 100 && touchDuration < 500) { // CRITICAL FIX: Reduced max duration from 1000ms to 500ms
        // User made a valid swipe - navigate to next/previous slide
      if (deltaX > 0) {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
        // FIXED: Don't call slide() after navigation - let prevSlide/nextSlide handle it
    } else {
        // NUCLEAR FIX: Direct snap back for maximum smoothness
        this.setTransform(this.getTranslateValue(), true);
      }
    }
    
    // FIXED: Reset touch state after navigation is complete
    setTimeout(() => {
      this.resetTouchState();
      
              // FIXED: Resume autoplay after navigation is complete
        if (this.autoplay && !this.hoverState.isHovered) {
          this.resumeAutoplay('manual');
        }
    }, 50);
  }
  
  resetTouchState() {
    if (!this.touchNavigationState) return;
    
    if (this.touchNavigationState) {
      this.touchNavigationState.isDragging = false;
      this.touchNavigationState.isVerticalScrolling = false;
      this.touchNavigationState.isTouching = false; // FIXED: Reset isTouching flag
    }
    
    if (this.wrapper) {
      this.wrapper.dataset.dragging = 'false';
    }
    
    if (this.track && this.track.style) {
      this.track.style.cursor = '';
    }
    
    if (this.touchNavigationState) {
      this.touchNavigationState.hasMoved = false;
      this.touchNavigationState.touchStartTime = 0;
    }

    
    setTimeout(() => {
      if (this.scrollBehavior !== 'continuous') {
        this.updateIndicators();
        this.updateNavigation();
      }
    }, 10);
    
    if (this.touchNavigationState && this.touchNavigationState.touchTimeout) {
      clearTimeout(this.touchNavigationState.touchTimeout);
      this.touchNavigationState.touchTimeout = null;
    }
  }

  // FIXED: UNIFIED CONTINUOUS MODE TOUCH HANDLING - NO CONFLICTS
  initContinuousTouchEvents() {
    let startX = 0;
    let startY = 0;
    let hasMoved = false;
    let originalPosition = 0;
    
    const handleTouchStart = (e) => {
      // FIXED: Clear any existing touch states to prevent conflicts
      if (this.touchNavigationState) {
        this.touchNavigationState.isDragging = false;
      }
      if (this.mobileTouchState) {
        this.mobileTouchState.isTouching = false;
        this.mobileTouchState.isPartialTouchHold = false;
      }
      
      startX = this.getPositionX(e);
      startY = this.getPositionY(e);
      hasMoved = false;
      // CRITICAL FIX: Validate continuous position
      originalPosition = (typeof this.continuousPosition === 'number' && !isNaN(this.continuousPosition)) ? this.continuousPosition : 0;
    };
    
    const handleTouchMove = (e) => {
      if (!startX) return;
      
      const currentX = this.getPositionX(e);
      const currentY = this.getPositionY(e);
      const deltaX = currentX - startX;
      const deltaY = currentY - startY;
      
      // Check if it's a horizontal swipe
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 20) {
        hasMoved = true;
        e.preventDefault();
      }
    };
    
    const handleTouchEnd = (e) => {
      if (!startX || !hasMoved) {
        startX = 0;
        return;
      }
      
      const currentX = this.getPositionX(e);
      const deltaX = currentX - startX;
      
      // Only trigger if it's a significant horizontal swipe
      if (Math.abs(deltaX) > 30) {
        // FIXED: Simplified pause and navigation logic
        this.pauseAutoplay('manual');
        
        // Calculate smooth navigation distance
        const slideDistance = this.slideWidthWithGap;
        let targetPosition;
        
        if (deltaX > 0) {
          // Swipe right - move to previous (positive direction)
          targetPosition = originalPosition + slideDistance;
        } else {
          // Swipe left - move to next (negative direction)
          targetPosition = originalPosition - slideDistance;
        }
        
        // FIXED: Simplified bounded navigation
        // CRITICAL FIX: Ensure slideWidthWithGap is calculated
        if (!this.slideWidthWithGap) {
          this.slideWidthWithGap = (this.slideWidth || 300) + (this.slideGap || 20);
        }
        const originalSlidesWidth = (this.slides && this.slides.length > 0) ? this.slides.length * this.slideWidthWithGap : 0;
        
        if (targetPosition > 0) {
          targetPosition = 0;
        } else if (targetPosition < -originalSlidesWidth) {
          targetPosition = -originalSlidesWidth;
        }
        
        // CRITICAL FIX: Apply smooth transition with bounds checking
        if (this.track && this.track.style) {
          this.track.style.transition = `transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
          // Ensure target position is within bounds
          const maxPosition = (this.slides && this.slides.length > 0) ? this.slides.length * this.slideWidthWithGap : 0;
          this.continuousPosition = Math.max(-maxPosition, Math.min(0, targetPosition));
          this.track.style.transform = `translateX(${this.continuousPosition}px)`;
        }
        
        // FIXED: Simplified resume logic
        setTimeout(() => {
          this.resumeAutoplay('manual');
        }, 400);
      }
      
      // Reset
      startX = 0;
      hasMoved = false;
    };
    
    // Add event listeners
    if (this.track && this.track.addEventListener) {
      this.track.addEventListener('touchstart', handleTouchStart, { passive: true });
      this.track.addEventListener('touchmove', handleTouchMove, { passive: false });
      this.track.addEventListener('touchend', handleTouchEnd);
      this.track.addEventListener('touchcancel', handleTouchEnd);
    }
  }
  
  getPositionX(e) {
    if (e && e.type && e.type.includes('mouse')) {
      return e.clientX;
    } else if (e && e.touches && e.touches.length > 0) {
      return e.touches[0].clientX;
    } else if (e && e.changedTouches && e.changedTouches.length > 0) {
      return e.changedTouches[0].clientX;
    }
    return 0;
  }
  
  getPositionY(e) {
    if (e && e.type && e.type.includes('mouse')) {
      return e.clientY;
    } else if (e && e.touches && e.touches.length > 0) {
      return e.touches[0].clientY;
    } else if (e && e.changedTouches && e.changedTouches.length > 0) {
      return e.changedTouches[0].clientY;
    }
    return 0;
  }
  
  prevSlide() {
    if (this.isTransitioning || this.scrollBehavior === 'continuous') return;
    
    this.currentIndex--;
    this.slide();
  }
  
  nextSlide() {
    if (this.isTransitioning || this.scrollBehavior === 'continuous') return;
    
    this.currentIndex++;
    this.slide();
  }
  
  goToSlide(index) {
    if (this.isTransitioning || this.scrollBehavior === 'continuous') return;
    
    if (this.scrollBehavior === 'smooth') {
      const clonesNeeded = Math.max(this.slidesPerView, 2);
      this.currentIndex = index + clonesNeeded;
    } else {
      this.currentIndex = index;
    }
    this.slide();
  }
  
  slide() {
    // NUCLEAR FIX: Allow slide() to be called during touch operations for manual navigation
    if (this.scrollBehavior === 'continuous') return;
    
    this.isTransitioning = true;
    
    // NUCLEAR FIX: Direct execution without requestAnimationFrame for maximum performance
    if (this.scrollBehavior === 'smooth') {
      this.setTransform(this.getTranslateValue());
      
      // NUCLEAR FIX: Direct setTimeout for better performance
      setTimeout(() => {
        const clonesNeeded = Math.max(this.slidesPerView, 2);
        
        if (this.currentIndex >= (this.slides && this.slides.length > 0 ? this.slides.length + clonesNeeded : clonesNeeded)) {
          this.currentIndex = clonesNeeded;
          this.setTransform(this.getTranslateValue(), false);
        } else if (this.currentIndex < clonesNeeded) {
          this.currentIndex = (this.slides && this.slides.length > 0 ? this.slides.length + clonesNeeded - 1 : clonesNeeded - 1);
          this.setTransform(this.getTranslateValue(), false);
        }
        this.isTransitioning = false;
      }, this.transitionDuration);
    } else {
      this.currentIndex = Math.max(0, Math.min(this.currentIndex, this.maxIndex));
      this.setTransform(this.getTranslateValue());
      
      // NUCLEAR FIX: Direct setTimeout for smooth completion
      setTimeout(() => {
        this.isTransitioning = false;
      }, Math.min(this.transitionDuration, 300));
    }
    
    this.updateIndicators();
    this.updateNavigation();
  }
  
  getTranslateValue() {
    // CRITICAL FIX: Ensure slideWidthWithGap is valid
    if (!this.slideWidthWithGap || isNaN(this.slideWidthWithGap) || this.slideWidthWithGap <= 0) {
      this.slideWidthWithGap = (this.slideWidth || 300) + (this.slideGap || 20);
    }
    return -this.currentIndex * this.slideWidthWithGap;
  }
  
  setTransform(value, transition = true) {
    if (!this.track) return;
    
    // NUCLEAR FIX: Direct execution without requestAnimationFrame for maximum performance
    if (this.touchNavigationState) {
      this.touchNavigationState.currentTranslate = value;
    }
    
    if (this.track && this.track.style) {
      // NUCLEAR FIX: Direct transition handling
    if (transition && this.scrollBehavior !== 'continuous') {
      this.track.style.transition = `transform ${this.transitionDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
    } else {
      this.track.style.transition = 'none';
    }
    
      // NUCLEAR FIX: Direct transform3d for maximum hardware acceleration
      this.track.style.transform = `translate3d(${value}px,0,0)`;
    }
  }
  
  updateIndicators() {
    if (!this.indicators || !this.indicators.length || this.scrollBehavior === 'continuous') return;
    
    let activeIndex;
    
    if (this.scrollBehavior === 'smooth') {
      const clonesNeeded = Math.max(this.slidesPerView, 2);
      activeIndex = (this.currentIndex - clonesNeeded + (this.slides && this.slides.length > 0 ? this.slides.length : 1)) % (this.slides && this.slides.length > 0 ? this.slides.length : 1);
    } else {
      activeIndex = this.currentIndex;
    }
    
    if (this.indicators && this.indicators.length > 0) {
    this.indicators.forEach((indicator, index) => {
      if (indicator && indicator.classList) {
      indicator.classList.toggle('active', index === activeIndex);
      }
    });
    }
  }
  
  updateNavigation() {
    if (!this.prevBtn || !this.nextBtn || this.scrollBehavior === 'continuous') return;
    
    if (this.scrollBehavior === 'smooth') {
      if (this.prevBtn) this.prevBtn.disabled = false;
      if (this.nextBtn) this.nextBtn.disabled = false;
    } else {
      if (this.prevBtn) this.prevBtn.disabled = this.currentIndex <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
    }
  }
  
  updateDisplay() {
    if (this.scrollBehavior === 'continuous') {
      this.hideNavigation();
      return;
    }
    
    if (!this.slides || this.slides.length <= this.slidesPerView) {
      this.hideNavigation();
      this.currentIndex = 0;
    } else {
      this.showNavigation();
      if (this.scrollBehavior === 'instant') {
        this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
      }
    }
    
    this.setTransform(this.getTranslateValue(), false);
    this.updateIndicators();
    this.updateNavigation();
  }
  
  startAutoplay() {
    if (!this.autoplay) return;
    
    this.pauseAutoplay();
    
    if (this.scrollBehavior === 'continuous') {
      this.startContinuousAnimation();
      return;
    }
    
    if (!this.slides || this.slides.length <= this.slidesPerView) return;
    
    // CRITICAL FIX: Pre-calculate interval for performance optimization
    let cachedInterval = null;
    let lastIntervalCalculation = 0;
    const INTERVAL_CACHE_DURATION = 200; // Cache for 200ms
    
    const getInterval = () => {
      const now = Date.now();
      
      // Use cached interval if recent (performance optimization)
      if (cachedInterval && (now - lastIntervalCalculation) < INTERVAL_CACHE_DURATION) {
        return cachedInterval;
      }
      
      // CRITICAL FIX: Simplified interval calculation for performance
      let baseInterval = this.speed || 3000;
      
      // User interaction adjustments (simplified)
      if ((this.hoverState && this.hoverState.isHovered) || 
          (this.mobileTouchState && (this.mobileTouchState.isTouching || this.mobileTouchState.isPartialTouchHold))) {
        baseInterval *= 3;
      }
      
      // Performance-based adjustments (simplified for speed)
      const performanceTier = (this.deviceCapabilities && this.deviceCapabilities.performanceTier) || 'standard';
      switch (performanceTier) {
        case 'ultra-premium':
        case 'premium':
          baseInterval *= 0.9;
          break;
        case 'basic':
        case 'minimal':
          baseInterval *= 1.5;
          break;
      }
      
      // Cache the result for performance
      cachedInterval = Math.max(baseInterval, 500);
      lastIntervalCalculation = now;
      
      return cachedInterval;
    };
    
    const autoplayLoop = () => {
      // NUCLEAR FIX: Ensure mobileTouchState exists before accessing properties
      if (!this.mobileTouchState) {
        this.mobileTouchState = {
          isTouching: false,
          isPartialTouchHold: false,
          touchStartTime: 0,
          touchHoldTimer: null,
          lastTouchPosition: null,
          touchMoveDistance: 0,
          isHorizontalScroll: false,
          isVerticalScrolling: false,
          scrollPauseTimer: null,
          shouldResumeFromUserPosition: false
        };
      }
      
      // NUCLEAR FIX: Direct execution without requestAnimationFrame for maximum performance
      // FIXED: Allow autoplay to continue after touch operations are complete
      if (!this.isTransitioning && 
          !this.mobileTouchState.isTouching && 
          (!this.hoverState || !this.hoverState.isHovered) && 
          !this.mobileTouchState.isPartialTouchHold) {
        this.nextSlide();
      }
      this.autoplayTimer = setTimeout(autoplayLoop, getInterval());
    };
    
    this.autoplayTimer = setTimeout(autoplayLoop, getInterval());
  }
  
  startContinuousAnimation() {
    if (this.scrollBehavior !== 'continuous' || !this.track || !this.slides || this.slides.length === 0) return;
    
    // CRITICAL FIX: Preserve position from manual navigation
    if (this.continuousPosition === undefined || this.continuousPosition === null) {
      this.continuousPosition = 0;
    }
    
    // CRITICAL FIX: Ensure continuous position is properly initialized
    if (typeof this.continuousPosition !== 'number' || isNaN(this.continuousPosition)) {
      this.continuousPosition = 0;
    }
    
    // FIXED: Don't override position if it was set by manual navigation
    // Only reset if it's the initial start
    if ((!this.autoplayState || !this.autoplayState.isPaused) && this.continuousPosition === 0) {
      this.continuousPosition = 0;
    }
    
    // NUCLEAR FIX: Pre-calculate all values for maximum performance
    const trackStyle = this.track.style;
    const slidesLength = this.slides.length;
    const slideWidthWithGap = this.slideWidthWithGap || ((this.slideWidth || 300) + (this.slideGap || 20));
    const originalSlidesWidth = slidesLength * slideWidthWithGap;
    
    // NUCLEAR FIX: Cache speed values
    let currentSpeed = 0.5;
    let isInteracting = false;
    
    // NUCLEAR FIX: Ultra-optimized animation loop with pre-calculated values
    const animate = () => {
      // NUCLEAR FIX: Minimal condition checks for maximum performance
      if (!this.autoplay || isInteracting) {
          this.continuousAnimationId = requestAnimationFrame(animate);
        return;
      }
      
      // NUCLEAR FIX: Direct position update without calculations
      this.continuousPosition -= currentSpeed;
      
      // NUCLEAR FIX: Direct transform update without string concatenation
      trackStyle.transform = `translate3d(${this.continuousPosition}px,0,0)`;
      
      // NUCLEAR FIX: Direct reset without calculations
      if (this.continuousPosition <= -originalSlidesWidth) {
        trackStyle.transition = 'none';
        this.continuousPosition = 0;
        trackStyle.transform = `translate3d(0,0,0)`;
      }
      
        this.continuousAnimationId = requestAnimationFrame(animate);
    };
    
    // NUCLEAR FIX: Update speed based on interactions (throttled)
    const updateSpeed = () => {
      const wasInteracting = isInteracting;
      isInteracting = (this.hoverState && this.hoverState.isHovered) || 
                     (this.mobileTouchState && (this.mobileTouchState.isTouching || this.mobileTouchState.isPartialTouchHold)) ||
                     (this.touchNavigationState && this.touchNavigationState.isDragging) ||
                     (this.mobileTouchState && (this.mobileTouchState.isHorizontalScroll || this.mobileTouchState.isVerticalScrolling)) ||
                     this.manualNavigationActive;
      
      if (isInteracting !== wasInteracting) {
        currentSpeed = isInteracting ? 0.15 : 0.5;
      }
    };
    
    // NUCLEAR FIX: Throttled speed updates
    setInterval(updateSpeed, 100);
    
    this.continuousAnimationId = requestAnimationFrame(animate);
  }
  
  // ISOLATED AUTOPLAY MANAGEMENT SYSTEM
  pauseAutoplay(reason = 'manual') {
    // CRITICAL FIX: Ensure autoplayState exists before accessing properties
    if (!this.autoplayState) {
      this.autoplayState = {
        isPaused: false,
        pauseReason: null,
        lastPauseTime: 0
      };
    }
    
    // Track pause reason for intelligent resume
    this.autoplayState.pauseReason = reason;
    this.autoplayState.isPaused = true;
    this.autoplayState.lastPauseTime = Date.now();
    
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    
    if (this.continuousAnimationId) {
      cancelAnimationFrame(this.continuousAnimationId);
      this.continuousAnimationId = null;
      
      // FIXED: Don't read transform position during pause to prevent position corruption
      // The continuousPosition is already correctly set by the manual navigation
    }
  }
  
  resumeAutoplay(reason = 'manual') {
    if (!this.autoplay) return;
    
    // CRITICAL FIX: Ensure autoplayState exists before accessing properties
    if (!this.autoplayState) {
      this.autoplayState = {
        isPaused: false,
        pauseReason: null,
        lastPauseTime: 0
      };
    }
    
    // INTELLIGENT RESUME: Only resume if the pause reason matches or is cleared
    if (this.autoplayState.pauseReason && this.autoplayState.pauseReason !== reason) {
      // Don't resume if paused by a different reason
      return;
    }
    
    // Check if any blocking conditions exist
    if ((this.mobileTouchState && this.mobileTouchState.isTouching) || (this.hoverState && this.hoverState.isHovered) || (this.mobileTouchState && this.mobileTouchState.isPartialTouchHold)) {
      return;
    }
    
    // Clear pause state
    this.autoplayState.isPaused = false;
    this.autoplayState.pauseReason = null;
    
    if (this.scrollBehavior === 'continuous') {
      this.startContinuousAnimation();
    } else {
      this.startAutoplay();
    }
  }
  
  // NEW: Resume autoplay from user's current position
  resumeAutoplayFromUserPosition() {
    if (!this.autoplay || this.scrollBehavior !== 'continuous') return;
    
    // CRITICAL FIX: Ensure mobileTouchState exists before accessing properties
    if (!this.mobileTouchState) {
      this.mobileTouchState = {
        isTouching: false,
        isPartialTouchHold: false,
        touchStartTime: 0,
        touchHoldTimer: null,
        lastTouchPosition: null,
        touchMoveDistance: 0,
        isHorizontalScroll: false,
        isVerticalScrolling: false,
        scrollPauseTimer: null,
        shouldResumeFromUserPosition: false
      };
    }
    
    this.mobileTouchState.shouldResumeFromUserPosition = true;
    this.resumeAutoplay();
  }
  
  hideNavigation() {
    if (this.prevBtn) this.prevBtn.style.display = 'none';
    if (this.nextBtn) this.nextBtn.style.display = 'none';
    if (this.indicators && this.indicators.length > 0) {
    this.indicators.forEach(indicator => indicator.style.display = 'none');
    }
  }
  
  showNavigation() {
    if (this.prevBtn) this.prevBtn.style.display = '';
    if (this.nextBtn) this.nextBtn.style.display = '';
    if (this.indicators && this.indicators.length > 0) {
    this.indicators.forEach(indicator => indicator.style.display = '');
    }
  }
  
  destroy() {
    // CRITICAL FIX: Complete cleanup to prevent memory leaks
    this.pauseAutoplay();
    
    // Clear all timers and intervals
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    
    if (this.continuousAnimationId) {
      cancelAnimationFrame(this.continuousAnimationId);
      this.continuousAnimationId = null;
    }
    
    if (this.mobileTouchState) {
      if (this.mobileTouchState.touchHoldTimer) {
        clearTimeout(this.mobileTouchState.touchHoldTimer);
        this.mobileTouchState.touchHoldTimer = null;
      }
      if (this.mobileTouchState.scrollPauseTimer) {
        clearTimeout(this.mobileTouchState.scrollPauseTimer);
        this.mobileTouchState.scrollPauseTimer = null;
      }
    }
    
    if (this.touchNavigationState && this.touchNavigationState.touchTimeout) {
      clearTimeout(this.touchNavigationState.touchTimeout);
      this.touchNavigationState.touchTimeout = null;
    }
    
    // Clear all monitoring intervals
    if (this.layoutMonitorInterval) {
      clearInterval(this.layoutMonitorInterval);
      this.layoutMonitorInterval = null;
    }
    
    if (this.basicLayoutInterval) {
      clearInterval(this.basicLayoutInterval);
      this.basicLayoutInterval = null;
    }
    
    if (this.systemImmunityIntervals && this.systemImmunityIntervals.length > 0) {
      this.systemImmunityIntervals.forEach(interval => clearInterval(interval));
      this.systemImmunityIntervals = [];
    }
    
    // Disconnect observers
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    if (this.contentObserver) {
      this.contentObserver.disconnect();
      this.contentObserver = null;
    }
    
    // Clean up style observer
    if (this.styleObserver) {
      this.styleObserver.disconnect();
      this.styleObserver = null;
    }
    
    // Remove all event listeners
    this.removeAllEventListeners();
    
    // Clean up system immunity
    this.cleanupSystemImmunity();
    
    // Clean up layout monitoring
    this.cleanupLayoutMonitoring();
    
    // CRITICAL FIX: Reset track styles with proper clone removal
    if (this.track) {
      // Remove clones in reverse order to prevent layout shifts
      const clones = this.track.querySelectorAll('.carousel-clone, .continuous-clone');
      for (let i = clones.length - 1; i >= 0; i--) {
        if (clones[i] && clones[i].parentNode) {
          clones[i].parentNode.removeChild(clones[i]);
        }
      }
    this.track.style.transform = '';
    this.track.style.transition = '';
      this.track.style.overflowX = '';
      this.track.style.scrollBehavior = '';
    }
    
    // Clear all references
    this.wrapper = null;
    this.track = null;
    this.slides = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.indicators = null;
  }
  
  // CRITICAL FIX: Remove all event listeners to prevent memory leaks
  removeAllEventListeners() {
    if (!this.wrapper) return;
    
    // Remove navigation button listeners
    if (this.prevBtn) {
      this.prevBtn.removeEventListener('click', this.prevSlide);
    }
    if (this.nextBtn) {
      this.nextBtn.removeEventListener('click', this.nextBtn);
    }
    
    // Remove indicator listeners
    if (this.indicators && this.indicators.length > 0) {
      this.indicators.forEach((indicator, index) => {
        indicator.removeEventListener('click', () => this.goToSlide(index));
      });
    }
    
    // Remove hover listeners
    this.wrapper.removeEventListener('mouseenter', this.handleMouseEnter);
    this.wrapper.removeEventListener('mouseleave', this.handleMouseLeave);
    
    // Remove touch listeners
    this.wrapper.removeEventListener('touchstart', this.handleMobileTouchStart);
    this.wrapper.removeEventListener('touchmove', this.handleMobileTouchMove);
    this.wrapper.removeEventListener('touchend', this.handleMobileTouchEnd);
    this.wrapper.removeEventListener('touchcancel', this.handleMobileTouchEnd);
    
    // Remove track listeners
    if (this.track) {
      this.track.removeEventListener('touchstart', this.touchStart);
      this.track.removeEventListener('touchmove', this.touchMove);
      this.track.removeEventListener('touchend', this.touchEnd);
      this.track.removeEventListener('touchcancel', this.touchEnd);
      this.track.removeEventListener('mousedown', this.touchStart);
      this.track.removeEventListener('mousemove', this.touchMove);
      this.track.removeEventListener('mouseup', this.touchEnd);
      this.track.removeEventListener('mouseleave', this.touchEnd);
    }
    
    // Remove visibility change listener
    if (document.removeEventListener) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }
  
  refresh() {
    if (!this.wrapper) return;
    
    this.slides = Array.from ? Array.from(this.wrapper.querySelectorAll('.carousel-reviews__slide:not(.carousel-clone):not(.continuous-clone)')) : [];
    this.indicators = Array.from ? Array.from(this.wrapper.querySelectorAll('.carousel-reviews__indicator')) : [];
    this.updateResponsiveSettings();
    this.updateDisplay();
    
    // Re-apply image protection after refresh
    this.applyImageProtection();
    
    // Re-apply star colors after refresh
    this.applyStarColors();
  }
  
  // ========================================
  // COMPREHENSIVE IMAGE DOWNLOAD PROTECTION
  // ========================================
  
  applyImageProtection() {
    if (!this.wrapper) return;
    
    // Get all images in the carousel
    const images = this.wrapper.querySelectorAll('img');
    
    images.forEach(img => {
      // Disable right-click context menu
      img.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
      
      // Disable drag and drop
      img.addEventListener('dragstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
      
      // Disable drag over
      img.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
      
      // Disable drop
      img.addEventListener('drop', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
      
      // Disable touch hold (mobile)
      img.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }, { passive: false });
      
      // Disable long press (mobile)
      img.addEventListener('touchend', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }, { passive: false });
      
      // Disable selection
      img.addEventListener('selectstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
      
      // Disable image saving via keyboard shortcuts
      img.addEventListener('keydown', (e) => {
        // Block Ctrl+S, Ctrl+A, Ctrl+C, F12, Ctrl+Shift+I, Ctrl+U
        if (e.ctrlKey && (e.key === 's' || e.key === 'a' || e.key === 'c' || e.key === 'u')) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      });
      
      // Add protective overlay
      this.addProtectiveOverlay(img);
      
      // Disable image loading from cache
      img.setAttribute('crossorigin', 'anonymous');
      
      // Add protection attributes
      img.setAttribute('draggable', 'false');
      img.setAttribute('oncontextmenu', 'return false;');
      img.setAttribute('onselectstart', 'return false;');
      img.setAttribute('onmousedown', 'return false;');
    });
    
    // Apply protection to image containers
    const imageContainers = this.wrapper.querySelectorAll('.review-card__image');
    imageContainers.forEach(container => {
      container.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
      
      container.addEventListener('dragstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
    });
  }
  
  // CRITICAL FIX: Apply star colors via JavaScript as fallback
  applyStarColors() {
    if (!this.wrapper) return;
    
    // Get computed styles to check if CSS variables are working
    const computedStyle = getComputedStyle(this.wrapper);
    const starColor = computedStyle.getPropertyValue('--review-star').trim();
    const starUnfilledColor = computedStyle.getPropertyValue('--review-star-unfilled').trim();
    
    // If CSS variables are not working, apply colors directly
    if (!starColor || starColor === '') {
      // Find all star elements
      const stars = this.wrapper.querySelectorAll('.review-star');
      
      stars.forEach(star => {
        if (star.classList.contains('review-star--filled')) {
          // Apply filled star color
          star.style.color = '#ffa500'; // Default orange
          star.style.setProperty('color', '#ffa500', 'important');
        } else {
          // Apply unfilled star color
          star.style.color = 'rgba(0, 0, 0, 0.2)'; // Default gray
          star.style.setProperty('color', 'rgba(0, 0, 0, 0.2)', 'important');
        }
      });
    }
  }
  
  // NUCLEAR FIX: Initialize nuclear-level performance monitoring
  initializePerformanceMonitoring() {
    if (!this.wrapper) return;
    
    // Performance monitoring variables
    this.performanceMetrics = {
      frameCount: 0,
      lastFrameTime: 0,
      averageFPS: 60,
      isLowPerformance: false,
      lastOptimizationTime: 0,
      smoothnessScore: 100,
      maxFPS: 0,
      minFPS: 60
    };
    
    // Monitor frame rate with nuclear-level optimization
    const monitorPerformance = (currentTime) => {
      this.performanceMetrics.frameCount++;
      
      if (this.performanceMetrics.frameCount % 15 === 0) { // Check every 15 frames for maximum responsiveness
        const elapsed = currentTime - this.performanceMetrics.lastFrameTime;
        this.performanceMetrics.averageFPS = Math.round(15000 / elapsed);
        this.performanceMetrics.lastFrameTime = currentTime;
        
        // Track max/min FPS
        this.performanceMetrics.maxFPS = Math.max(this.performanceMetrics.maxFPS, this.performanceMetrics.averageFPS);
        this.performanceMetrics.minFPS = Math.min(this.performanceMetrics.minFPS, this.performanceMetrics.averageFPS);
        
        // Calculate smoothness score
        this.performanceMetrics.smoothnessScore = Math.min(100, this.performanceMetrics.averageFPS * 1.67);
        
        // Adjust performance based on FPS (nuclear-responsive)
        if (currentTime - this.performanceMetrics.lastOptimizationTime > 500) {
          if (this.performanceMetrics.averageFPS < 50) {
            this.performanceMetrics.isLowPerformance = true;
            this.optimizeForLowPerformance();
          } else if (this.performanceMetrics.averageFPS > 58) {
            this.performanceMetrics.isLowPerformance = false;
            this.optimizeForHighPerformance();
          }
          this.performanceMetrics.lastOptimizationTime = currentTime;
        }
      }
      
      if (this.autoplay) {
        requestAnimationFrame(monitorPerformance);
      }
    };
    
    this.performanceMetrics.lastFrameTime = performance.now();
    requestAnimationFrame(monitorPerformance);
  }
  
  // CRITICAL FIX: Optimize for low-performance devices
  optimizeForLowPerformance() {
    // Reduce animation frequency for stability
    if (this.scrollBehavior === 'continuous') {
      // Reduce continuous animation speed for smoother performance
      this.continuousAnimationSpeed = Math.max(this.continuousAnimationSpeed * 0.7, 0.2);
    } else {
      // Increase autoplay interval for stability
      this.speed = Math.min(this.speed * 1.3, 10000);
    }
  }
  
  // CRITICAL FIX: Optimize for high-performance devices
  optimizeForHighPerformance() {
    // Increase animation frequency for ultra-smooth performance
    if (this.scrollBehavior === 'continuous') {
      // Increase continuous animation speed for maximum smoothness
      this.continuousAnimationSpeed = Math.min(this.continuousAnimationSpeed * 1.2, 1.5);
    } else {
      // Decrease autoplay interval for faster, smoother transitions
      this.speed = Math.max(this.speed * 0.8, 1500);
    }
  }
  
  addProtectiveOverlay(img) {
    // Create invisible overlay to block interactions
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: transparent;
      z-index: 9999;
      pointer-events: none;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    `;
    
    // Make image container relative if not already
    const container = img.parentElement;
    if (container && container.style.position !== 'relative') {
      container.style.position = 'relative';
    }
    
    // Add overlay to container
    if (container) {
      container.appendChild(overlay);
    }
  }
  
  // Global keyboard shortcut protection
  initGlobalProtection() {
    // Block common keyboard shortcuts for saving images
    document.addEventListener('keydown', (e) => {
      // Only apply to carousel area
      if (!this.wrapper || !this.wrapper.contains(e.target)) return;
      
      // Block Ctrl+S (Save)
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block Ctrl+A (Select All)
      if (e.ctrlKey && e.key === 'a') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block Ctrl+C (Copy)
      if (e.ctrlKey && e.key === 'c') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block F12 (Developer Tools)
      if (e.key === 'F12') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block Ctrl+Shift+I (Developer Tools)
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block Ctrl+U (View Source)
      if (e.ctrlKey && e.key === 'u') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block Ctrl+Shift+C (Inspect Element)
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    }, true);
    
    // Block right-click globally on carousel
    this.wrapper.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });
    
    // Block drag and drop globally on carousel
    this.wrapper.addEventListener('dragstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });
    
    this.wrapper.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });
    
    this.wrapper.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });
  }
}

// Initialize all carousel instances
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('.carousel-reviews__wrapper');
  carousels.forEach(carousel => {
    if (carousel) {
    const instance = new CarouselReviews(carousel);
    carousel.carouselInstance = instance;
    }
  });
});

// Handle Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const carousel = event.target.querySelector('.carousel-reviews__wrapper');
    if (carousel) {
      const instance = new CarouselReviews(carousel);
      carousel.carouselInstance = instance;
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const carousel = event.target.querySelector('.carousel-reviews__wrapper');
    if (carousel && carousel.carouselInstance) {
      carousel.carouselInstance.destroy();
    }
  });
  
  document.addEventListener('shopify:section:select', (event) => {
    const carousel = event.target.querySelector('.carousel-reviews__wrapper');
    if (carousel && carousel.carouselInstance) {
      carousel.carouselInstance.pauseAutoplay();
    }
  });
  
  document.addEventListener('shopify:section:deselect', (event) => {
    const carousel = event.target.querySelector('.carousel-reviews__wrapper');
    if (carousel && carousel.carouselInstance) {
      carousel.carouselInstance.resumeAutoplay();
    }
  });
  
  document.addEventListener('shopify:block:select', (event) => {
    const carousel = event.target.closest('.carousel-reviews__wrapper');
    if (carousel && carousel.carouselInstance) {
      carousel.carouselInstance.refresh();
    }
  });
}